import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Timestamp, Value } from '@bufbuild/protobuf';
import { Code, ConnectError } from '@connectrpc/connect';
import { pathFor } from '@mono/app-routes';
import { ExecutionAuthorityBasis as Basis, ExecutionCommercialTreatment as Commercial, ContractModelTypes, IntelligenceTypes, ProjectDataTypes, ExecutionScopeAssessment as Scope } from '@mono/proto-clients';
import { SpecraButton, SpecraDataTable, SpecraLink, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { QueryClientContext } from '@tanstack/react-query';
import { Check, ClipboardCheck, MessageSquare, X } from 'lucide-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { assistWriteHeaders } from '../../../lib/assistAttribution';
import { agentClient, artifactClient, contractModelClient, deliveryClient, intelligenceClient, projectDataClient } from '../../../lib/connect/clients';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { parseLocalDate, parseUtcDate } from '../../../pages/Delivery/_shared/api/nullable';
import { cmDate } from '../../../pages/Delivery/Deliverables/lib/format';
import { startSowIntake } from '../../../pages/Delivery/Docs/useUploadSowDocument';
import { addGlossaryLink, glossaryKey } from '../../../pages/Delivery/Glossary/hooks';
import { patchProgram } from '../../../pages/Delivery/Programs/programPatch';
import { useProgramWriteAccess } from '../../../pages/Delivery/Programs/programWriteAccess';
import { useChatFeedback } from '../ChatFeedbackContext';
import { applyCaptureAction } from './applyCaptureAction';
import { applyUploadParityAction } from './applyDeliveryUpload';
import { attachResolvedChatFiles, chatFileIdsFromParams, createEvidenceProgramDocument, resolveChatFiles } from './assistChatFiles';
import { extractQualityScoreParams } from './assistExtractQuality';
import { BulkActionSummary } from './BulkActionSummary';
import { alreadyHandled, runBulkApply } from './deliveryActionBulk';
import { cardText, visibleOptionalColumns } from './deliveryActionColumns';
import { deliveryActionNeedsProgram, deliveryFileMetaByAction, isAttachmentDocumentCategory, isContractDocumentCategory, isSowDocumentCategory, normalizeProgramDocumentCategory, receiptHrefForAction, sowReceiptHref, verdictForGate } from './deliveryActionDestinations';
import { groupPlanRows, isPendingResult, isPlanWriteRow, PLAN_PAGE_SIZE, planReceiptText, planRowKind, rowProgramId } from './deliveryActionPlan';
import { resolveDeliveryActionParams } from './deliveryActionRowRefs';
import { strListFiles } from './deliveryEvidenceFiles';
import { isPricingStage, PricingSheetStageList } from './PricingSheetStageList';
import { resolvePlatformUserId } from './resolvePlatformUser';
import { useDeliveryUploadAuthz } from './useDeliveryUploadAuthz';
// SAD-870 / FND-27: browser-applied Assist writes are labelled (never authorized)
// by X-Specra-Via, plus X-Specra-Assist-Run when the saved proposal is known.
const ASSIST_VIA_ONLY_HEADERS = {
    'X-Specra-Via': 'assist'
};
/** Map saved approvalState (+ optional commented UI-only) onto card status. */ export function cardStatusFromOutput(output) {
    switch(output.approvalState){
        case 'partial':
            // A plan with nothing failed is still open: duplicates or questions wait
            // for the user. It is not a retry.
            if (output._planFiling && !(output.rowResults ?? []).some((r)=>r?.status === 'fail')) {
                return 'pending';
            }
            return 'partial';
        case 'accepted':
        case 'rejected':
            return output.approvalState;
        default:
            return 'pending';
    }
}
function normalizeRowResults(actions, rows, planFiling = false) {
    if (!rows?.length) return [];
    return actions.map((_, i)=>{
        const row = rows[i];
        if (row?.status === 'ok' || row?.status === 'fail' || row?.status === 'skipped' || row?.status === 'pending') {
            return {
                status: row.status,
                message: row.message,
                apply: row.apply
            };
        }
        // A plan row with no stored outcome was never decided — never a failure.
        return planFiling ? {
            status: 'pending'
        } : {
            status: 'fail',
            message: ''
        };
    });
}
function countRowResults(rows) {
    let ok = 0;
    let failed = 0;
    let skipped = 0;
    for (const row of rows){
        if (row.status === 'ok') ok++;
        else if (row.status === 'fail') failed++;
        else if (row.status === 'skipped') skipped++;
    }
    return {
        ok,
        failed,
        skipped
    };
}
function str(params, key) {
    const v = params?.[key];
    return typeof v === 'string' ? v : '';
}
/** First non-empty string among param keys (aliases). */ function strAny(params, ...keys) {
    for (const key of keys){
        const v = str(params, key);
        if (v) return v;
    }
    return '';
}
function bool(params, key) {
    return params?.[key] === true;
}
/** Plain labels for score-field names that otherwise leak into failure rows. */ const SCORE_FIELD_LABELS = {
    report_period_start: 'report period start',
    report_period_end: 'report period end',
    reportPeriodStart: 'report period start',
    reportPeriodEnd: 'report period end',
    eval_period_start: 'evaluation period start',
    eval_period_end: 'evaluation period end',
    evalPeriodStart: 'evaluation period start',
    evalPeriodEnd: 'evaluation period end',
    quarter: 'quarter'
};
/** Map ryugu/pangaea errors to plain Assist copy. */ export function humanizeDeliveryActionError(err) {
    const raw = apiErrorMessage(err, 'One or more actions failed');
    let plain = raw;
    for (const [token, label] of Object.entries(SCORE_FIELD_LABELS)){
        plain = plain.split(token).join(label);
    }
    const lower = plain.toLowerCase();
    if (lower.includes('dual control') || lower.includes('proposer cannot approve') || lower.includes('self-approval') || lower.includes('self approval')) {
        return 'You proposed this, so someone else has to approve it.';
    }
    if (lower.includes('changed after this edit was proposed')) {
        return 'The waiver changed after this edit was proposed, so it was not applied. Propose the edit again.';
    }
    if (lower.includes('permission') || lower.includes('not authorized') || lower.includes('unauthorized') || lower.includes('forbidden') || lower.includes('access denied')) {
        return "You don't have permission to do that on this program.";
    }
    if (lower.includes('wrong state') || lower.includes('invalid state') || lower.includes('not open')) {
        return 'That record is not in a state that allows this change.';
    }
    if (lower.includes('cannot be the same person who conducted') || lower.includes('same person who conducted the audit')) {
        return 'The reviewer cannot be the same person who conducted the audit.';
    }
    if (/^http 5\d\d\b/.test(lower) || lower.includes('failed to fetch') || lower.includes('unavailable') || err instanceof ConnectError && err.code === Code.Unavailable) {
        return 'Specra could not reach Delivery, so this row did not finish. Retry in a moment.';
    }
    return plain;
}
function applyResultStrings(fields) {
    const out = {};
    for (const [k, v] of Object.entries(fields)){
        if (v == null || v === '') continue;
        if (Array.isArray(v)) {
            out[k] = v.map(String).join(',');
            continue;
        }
        out[k] = String(v);
    }
    return out;
}
function num(params, key) {
    const v = params?.[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
function numAny(params, ...keys) {
    for (const key of keys){
        const v = num(params, key);
        if (v !== undefined) return v;
    }
    return undefined;
}
function optionalCents(params) {
    const v = num(params, 'value_cents');
    return v === undefined ? undefined : BigInt(Math.round(v));
}
function strList(params, key) {
    const v = params?.[key];
    if (!Array.isArray(v)) return [];
    return v.filter((x)=>typeof x === 'string' && x.trim() !== '');
}
function numList(params, key) {
    const v = params?.[key];
    if (!Array.isArray(v)) return [];
    return v.filter((x)=>typeof x === 'number' && Number.isFinite(x));
}
function hasParam(params, ...keys) {
    if (!params) return false;
    // Object.hasOwn needs ES2022 lib; getOwnPropertyDescriptor is the same check.
    return keys.some((k)=>Object.getOwnPropertyDescriptor(params, k) != null);
}
function ymdTimestamp(ymd) {
    const s = ymd.trim();
    if (!s) return undefined;
    return Timestamp.fromDate(parseLocalDate(s));
}
// Contract-imported calendar dates (periods of performance, optional-task
// exercises) are stored at UTC midnight, and their pages write them that way
// (parseUtcDate). Writing them as local midnight shifted the stored instant
// on every Assist edit (SAD-941 live check).
function ymdUtcTimestamp(ymd) {
    const s = ymd.trim();
    if (!s) return undefined;
    return Timestamp.fromDate(parseUtcDate(s));
}
function dutyFieldsFromParams(params) {
    return {
        title: str(params, 'title'),
        pwsRef: strAny(params, 'pws_ref', 'pwsRef'),
        letter: str(params, 'letter'),
        aliases: strList(params, 'aliases'),
        description: str(params, 'description'),
        format: str(params, 'format'),
        submissionMethod: strAny(params, 'submission_method', 'submissionMethod'),
        acceptanceCriteria: strAny(params, 'acceptance_criteria', 'acceptanceCriteria'),
        finalReviewTier: strAny(params, 'final_review_tier', 'finalReviewTier'),
        sourceDocumentId: strAny(params, 'source_document_id', 'sourceDocumentId'),
        sourceLocator: strAny(params, 'source_locator', 'sourceLocator'),
        eventKind: strAny(params, 'event_kind', 'eventKind')
    };
}
function scheduleFieldsFromParams(params) {
    return {
        clinId: strAny(params, 'clin_id', 'clinId'),
        gate: str(params, 'gate'),
        periodOfPerformanceId: strAny(params, 'period_of_performance_id', 'periodOfPerformanceId'),
        optionalTaskId: strAny(params, 'optional_task_id', 'optionalTaskId'),
        kind: str(params, 'kind'),
        dueOn: strAny(params, 'due_on', 'dueOn'),
        hasOffset: bool(params, 'has_offset') || bool(params, 'hasOffset'),
        offsetN: numAny(params, 'offset_n', 'offsetN') ?? 0,
        offsetUnit: strAny(params, 'offset_unit', 'offsetUnit'),
        cadence: str(params, 'cadence'),
        cadenceInterval: numAny(params, 'cadence_interval', 'cadenceInterval') ?? 0,
        dayOrdinals: numList(params, 'day_ordinals').length ? numList(params, 'day_ordinals') : numList(params, 'dayOrdinals'),
        dayWeekday: strAny(params, 'day_weekday', 'dayWeekday'),
        dayBasis: strAny(params, 'day_basis', 'dayBasis'),
        eventKind: strAny(params, 'event_kind', 'eventKind'),
        subjects: strList(params, 'subjects'),
        ruleText: strAny(params, 'rule_text', 'ruleText'),
        sourceDocumentId: strAny(params, 'source_document_id', 'sourceDocumentId'),
        sourceLocator: strAny(params, 'source_locator', 'sourceLocator'),
        effectiveFrom: strAny(params, 'effective_from', 'effectiveFrom'),
        effectiveTo: strAny(params, 'effective_to', 'effectiveTo'),
        establishedByModificationId: strAny(params, 'established_by_modification_id', 'establishedByModificationId')
    };
}
function sowGapChoice(raw) {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        if (raw === 1) return ContractModelTypes.SowGapChoice.LATEST_SOW;
        if (raw === 2) return ContractModelTypes.SowGapChoice.AWARD;
        if (raw === 3) return ContractModelTypes.SowGapChoice.UPLOAD_EARLIER;
        return ContractModelTypes.SowGapChoice.UNSPECIFIED;
    }
    const s = String(raw ?? '').trim().toUpperCase().replace(/^SOW_GAP_CHOICE_/, '');
    switch(s){
        case 'LATEST_SOW':
        case '1':
            return ContractModelTypes.SowGapChoice.LATEST_SOW;
        case 'AWARD':
        case '2':
            return ContractModelTypes.SowGapChoice.AWARD;
        case 'UPLOAD_EARLIER':
        case '3':
            return ContractModelTypes.SowGapChoice.UPLOAD_EARLIER;
        default:
            return ContractModelTypes.SowGapChoice.UNSPECIFIED;
    }
}
function proposalReviewState(raw) {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        if (raw === 1) return IntelligenceTypes.ProposalReviewState.PENDING;
        if (raw === 2) return IntelligenceTypes.ProposalReviewState.ACCEPTED;
        if (raw === 3) return IntelligenceTypes.ProposalReviewState.REJECTED;
        if (raw === 4) return IntelligenceTypes.ProposalReviewState.USER_EDITED;
        return IntelligenceTypes.ProposalReviewState.UNSPECIFIED;
    }
    const s = String(raw ?? '').trim().toUpperCase().replace(/^PROPOSAL_REVIEW_STATE_/, '');
    switch(s){
        case 'PENDING':
        case '1':
            return IntelligenceTypes.ProposalReviewState.PENDING;
        case 'ACCEPTED':
        case '2':
            return IntelligenceTypes.ProposalReviewState.ACCEPTED;
        case 'REJECTED':
        case '3':
            return IntelligenceTypes.ProposalReviewState.REJECTED;
        case 'USER_EDITED':
        case '4':
            return IntelligenceTypes.ProposalReviewState.USER_EDITED;
        default:
            return IntelligenceTypes.ProposalReviewState.UNSPECIFIED;
    }
}
function reviewedValueFromParam(raw) {
    if (raw == null || raw === '') return undefined;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return new Value({
            kind: {
                case: 'numberValue',
                value: raw
            }
        });
    }
    if (typeof raw === 'boolean') {
        return new Value({
            kind: {
                case: 'boolValue',
                value: raw
            }
        });
    }
    if (typeof raw === 'string') {
        return new Value({
            kind: {
                case: 'stringValue',
                value: raw
            }
        });
    }
    return undefined;
}
/**
 * Runs `make` once per key for one card: a bulk card shares one chat file
 * across hundreds of rows, and each row must not copy it again.
 */ function cachedOnce(cache, key, make) {
    if (!cache) return make();
    const hit = cache.get(key);
    if (hit) return hit;
    const made = make();
    cache.set(key, made);
    // A failed copy is retried by the next row, not remembered.
    made.catch(()=>cache.delete(key));
    return made;
}
function chatFileKey(params) {
    return chatFileIdsFromParams(params).join(',');
}
async function resolveSubmissionAttachments(params, programId, cache) {
    const p = params ?? {};
    const resolved = await cachedOnce(cache, `attach|${programId}|${chatFileKey(p)}`, async ()=>attachResolvedChatFiles(await resolveChatFiles(p), {
            programId
        }));
    const attachments = resolved.map((f)=>({
            artifactId: f.artifactId,
            label: f.filename
        }));
    const linkUrl = strAny(p, 'link_url', 'linkUrl');
    if (linkUrl) {
        attachments.push({
            url: linkUrl,
            label: strAny(p, 'link_label', 'linkLabel') || undefined
        });
    }
    return attachments;
}
/** YYYY-MM from a protobuf Timestamp (UTC calendar month). */ function periodMonthFromTimestamp(ts) {
    if (!ts) return '';
    try {
        const d = ts.toDate();
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
    } catch  {
        return '';
    }
}
function authorityBasis(raw) {
    switch(raw.trim().toLowerCase()){
        case 'award':
            return Basis.AWARD;
        case 'modification':
            return Basis.MODIFICATION;
        case 'incorporated_scope_artifact':
            return Basis.INCORPORATED_SCOPE_ARTIFACT;
        case 'standalone_scope_artifact':
            return Basis.STANDALONE_SCOPE_ARTIFACT;
        case 'work_authorization':
            return Basis.WORK_AUTHORIZATION;
        case 'written_direction':
            return Basis.WRITTEN_DIRECTION;
        case 'email_concurrence':
            return Basis.EMAIL_CONCURRENCE;
        case 'internal_plan':
            return Basis.INTERNAL_PLAN;
        case 'manual_assertion':
            return Basis.MANUAL_ASSERTION;
        case 'unknown':
            return Basis.UNKNOWN;
        default:
            return Basis.UNSPECIFIED;
    }
}
function scopeAssessment(raw) {
    switch(raw.trim().toLowerCase()){
        case 'in_scope':
            return Scope.IN_SCOPE;
        case 'potential_scope_growth':
            return Scope.POTENTIAL_SCOPE_GROWTH;
        case 'out_of_scope':
            return Scope.OUT_OF_SCOPE;
        case 'disputed':
            return Scope.DISPUTED;
        case 'unknown':
            return Scope.UNKNOWN;
        default:
            return Scope.UNSPECIFIED;
    }
}
function commercialTreatment(raw) {
    switch(raw.trim().toLowerCase()){
        case 'separately_invoiced':
            return Commercial.SEPARATELY_INVOICED;
        case 'included_in_clin_price':
            return Commercial.INCLUDED_IN_CLIN_PRICE;
        case 'not_separately_priced':
            return Commercial.NOT_SEPARATELY_PRICED;
        case 'non_billable':
            return Commercial.NON_BILLABLE;
        case 'unfunded':
            return Commercial.UNFUNDED;
        case 'potential_change':
            return Commercial.POTENTIAL_CHANGE;
        case 'disputed':
            return Commercial.DISPUTED;
        case 'unknown':
            return Commercial.UNKNOWN;
        default:
            return Commercial.UNSPECIFIED;
    }
}
/** Apply one allowlisted Delivery action under the human's JWT (SAD-870). */ export async function applyDeliveryAction(programId, row, options) {
    let params = {
        ...row.params ?? {}
    };
    if (row.action === 'add_workstream_member') {
        const resolved = await resolvePlatformUserId(params);
        if (resolved.error) {
            throw new Error(resolved.error);
        }
        if (resolved.userId) {
            params = {
                ...params,
                user_id: resolved.userId
            };
        }
    }
    const bodyBase = {
        programId
    };
    const assistHeaders = options?.assistRun ? assistWriteHeaders(options.assistRun.conversationId, options.assistRun.toolCallId) : ASSIST_VIA_ONLY_HEADERS;
    const opts = {
        headers: assistHeaders
    };
    switch(row.action){
        case 'propose_waiver':
            await contractModelClient.proposeOccurrenceWaiver({
                body: {
                    ...bodyBase,
                    occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                    note: strAny(params, 'note', 'reason', 'message'),
                    // Set only when the COR already agreed: then it waits only for a
                    // second person.
                    reason: str(params, 'cor_reason'),
                    waivedOn: str(params, 'cor_date')
                }
            }, opts);
            return {};
        case 'propose_extension':
            await contractModelClient.proposeOccurrenceExtension({
                body: {
                    ...bodyBase,
                    occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                    dueOn: strAny(params, 'due_on', 'dueOn'),
                    note: strAny(params, 'note', 'reason', 'message'),
                    reason: str(params, 'cor_reason'),
                    corAgreedOn: str(params, 'cor_date')
                }
            }, opts);
            return {};
        case 'withdraw_proposal':
        case 'reject_decision':
            await contractModelClient.rejectOccurrenceDecision({
                body: {
                    ...bodyBase,
                    changeSetId: strAny(params, 'change_set_id', 'changeSetId'),
                    reason: str(params, 'reason')
                }
            }, opts);
            return {};
        case 'mark_not_owed':
            await contractModelClient.proposeOccurrenceNotOwed({
                body: {
                    ...bodyBase,
                    occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                    reason: str(params, 'reason'),
                    note: str(params, 'note') || 'Marked not owed from Specra Assist'
                }
            }, opts);
            return {};
        case 'move_date':
            await contractModelClient.moveOccurrence({
                body: {
                    ...bodyBase,
                    occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                    dueOn: strAny(params, 'due_on', 'dueOn'),
                    reason: str(params, 'reason')
                }
            }, opts);
            return {};
        case 'add_date':
            await contractModelClient.addOccurrence({
                body: {
                    ...bodyBase,
                    dutyId: strAny(params, 'duty_id', 'dutyId'),
                    dueOn: strAny(params, 'due_on', 'dueOn'),
                    label: str(params, 'label'),
                    workstreamId: strAny(params, 'workstream_id', 'workstreamId'),
                    periodOfPerformanceId: strAny(params, 'period_of_performance_id', 'periodOfPerformanceId')
                }
            }, opts);
            return {};
        case 'comment':
        case 'reply':
            await contractModelClient.createComment({
                body: {
                    ...bodyBase,
                    targetType: strAny(params, 'target_type', 'targetType'),
                    targetId: strAny(params, 'target_id', 'targetId'),
                    parentId: strAny(params, 'parent_id', 'parentId'),
                    body: str(params, 'body')
                }
            }, opts);
            return {};
        case 'upvote':
            await contractModelClient.setCommentUpvote({
                body: {
                    ...bodyBase,
                    commentId: strAny(params, 'comment_id', 'commentId'),
                    upvoted: bool(params, 'upvoted')
                }
            }, opts);
            return {};
        case 'add_glossary_link':
            {
                const rel = await addGlossaryLink(programId, {
                    fromTermId: strAny(params, 'from_term_id', 'fromTermId'),
                    fromWorkstreamId: strAny(params, 'from_workstream_id', 'fromWorkstreamId'),
                    toTermId: strAny(params, 'to_term_id', 'toTermId'),
                    toWorkstreamId: strAny(params, 'to_workstream_id', 'toWorkstreamId'),
                    relation: str(params, 'relation'),
                    reason: str(params, 'reason'),
                    sourceDocumentId: strAny(params, 'source_document_id', 'sourceDocumentId'),
                    sourcePage: Number(params.source_page ?? params.sourcePage ?? 0) || 0,
                    replacesRelationId: strAny(params, 'replaces_relation_id', 'replacesRelationId')
                }, opts);
                return {
                    id: rel?.id ?? '',
                    href: pathFor('program.glossary', {
                        programId
                    })
                };
            }
        case 'record_cor_answer':
            {
                let documentId = strAny(params, 'document_id', 'documentId');
                const corKey = chatFileKey(params);
                if (!documentId && corKey) {
                    // One COR document per file, however many answers it backs.
                    documentId = await cachedOnce(options?.fileCache, `cordoc|${programId}|${corKey}`, async ()=>{
                        const corFiles = await attachResolvedChatFiles(await resolveChatFiles(params), {
                            programId
                        });
                        return corFiles[0] ? createEvidenceProgramDocument(programId, corFiles[0], {
                            headers: assistHeaders
                        }) : '';
                    });
                }
                await contractModelClient.recordOccurrenceDecisionCorAnswer({
                    body: {
                        ...bodyBase,
                        changeSetId: strAny(params, 'change_set_id', 'changeSetId'),
                        agreed: bool(params, 'agreed'),
                        corOn: strAny(params, 'cor_date', 'cor_on', 'cor_agreed_on', 'corAgreedOn'),
                        reason: str(params, 'reason'),
                        documentId
                    }
                }, opts);
                return {};
            }
        case 'approve_decision':
            await contractModelClient.approveOccurrenceDecision({
                body: {
                    ...bodyBase,
                    changeSetId: strAny(params, 'change_set_id', 'changeSetId')
                }
            }, opts);
            return {};
        case 'remove_date':
            await contractModelClient.removeOccurrence({
                body: {
                    ...bodyBase,
                    occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId')
                }
            }, opts);
            return {};
        case 'delete_comment':
            await contractModelClient.deleteComment({
                body: {
                    ...bodyBase,
                    commentId: strAny(params, 'comment_id', 'commentId')
                }
            }, opts);
            return {};
        case 'create_submission':
            {
                const validated = await resolveChatFiles(params);
                const resolved = await attachResolvedChatFiles(validated, {
                    programId
                });
                const attachments = resolved.map((f)=>({
                        artifactId: f.artifactId,
                        label: f.filename
                    }));
                const linkUrl = strAny(params, 'link_url', 'linkUrl');
                if (linkUrl) {
                    attachments.push({
                        url: linkUrl,
                        label: strAny(params, 'link_label', 'linkLabel') || undefined
                    });
                }
                if (attachments.length === 0) {
                    throw new Error('provide files (chat attachment ids from list-files) and/or link_url');
                }
                // Prefer an explicit agent param; otherwise the human who Accepted
                // (same default as the Submit drawer "Me" path).
                const submittedByDisplay = strAny(params, 'submitted_by_display', 'submittedByDisplay') || (options?.acceptorDisplay ?? '').trim();
                await contractModelClient.createSubmission({
                    body: {
                        ...bodyBase,
                        occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                        alsoOccurrenceIds: strList(params, 'also_occurrence_ids'),
                        submittedOn: strAny(params, 'submitted_on', 'submittedOn'),
                        submittedByDisplay,
                        note: strAny(params, 'note', 'reason', 'message'),
                        attachments
                    }
                }, opts);
                return {};
            }
        case 'withdraw_submission':
            await contractModelClient.withdrawSubmission({
                body: {
                    ...bodyBase,
                    submissionId: strAny(params, 'submission_id', 'submissionId'),
                    reason: str(params, 'reason')
                }
            }, opts);
            return {};
        case 'record_review':
            await contractModelClient.recordReview({
                body: {
                    ...bodyBase,
                    submissionId: strAny(params, 'submission_id', 'submissionId'),
                    tier: str(params, 'tier'),
                    decision: str(params, 'decision'),
                    reviewerDisplay: strAny(params, 'reviewer_display', 'reviewerDisplay'),
                    decidedOn: strAny(params, 'decided_on', 'decidedOn'),
                    notes: strAny(params, 'notes', 'note', 'reason')
                }
            }, opts);
            return {};
        case 'create_workstream':
            {
                const resp = await contractModelClient.createWorkstream({
                    body: {
                        ...bodyBase,
                        name: str(params, 'name'),
                        ownerContactId: strAny(params, 'owner_contact_id', 'ownerContactId'),
                        lineNumbers: strList(params, 'line_numbers'),
                        aliases: strList(params, 'aliases')
                    }
                }, opts);
                const id = resp.body?.workstream?.id ?? '';
                return id ? {
                    id,
                    workstream_id: id,
                    workstreamId: id
                } : {};
            }
        case 'update_workstream':
            await contractModelClient.updateWorkstream({
                body: {
                    ...bodyBase,
                    workstreamId: strAny(params, 'workstream_id', 'workstreamId'),
                    name: str(params, 'name') || undefined,
                    ownerContactId: strAny(params, 'owner_contact_id', 'ownerContactId') || undefined
                }
            }, opts);
            return {};
        case 'replace_workstream_lines':
            await contractModelClient.updateWorkstream({
                body: {
                    ...bodyBase,
                    workstreamId: strAny(params, 'workstream_id', 'workstreamId'),
                    replaceLines: true,
                    lineNumbers: strList(params, 'line_numbers')
                }
            }, opts);
            return {};
        case 'add_workstream_member':
            {
                const resp = await contractModelClient.addWorkstreamMember({
                    body: {
                        ...bodyBase,
                        workstreamId: strAny(params, 'workstream_id', 'workstreamId'),
                        userId: strAny(params, 'user_id', 'userId'),
                        contactId: strAny(params, 'contact_id', 'contactId'),
                        role: str(params, 'role') || 'member'
                    }
                }, opts);
                const id = resp.body?.member?.id ?? '';
                return id ? {
                    id,
                    member_id: id,
                    memberId: id
                } : {};
            }
        case 'update_workstream_member':
            await contractModelClient.updateWorkstreamMember({
                body: {
                    ...bodyBase,
                    memberId: strAny(params, 'member_id', 'memberId'),
                    role: str(params, 'role')
                }
            }, opts);
            return {};
        case 'remove_workstream_member':
            await contractModelClient.removeWorkstreamMember({
                body: {
                    ...bodyBase,
                    memberId: strAny(params, 'member_id', 'memberId')
                }
            }, opts);
            return {};
        case 'create_work_product':
            {
                const wpFiles = await attachResolvedChatFiles(await resolveChatFiles(params), {
                    programId
                });
                await contractModelClient.createWorkProduct({
                    body: {
                        ...bodyBase,
                        workstreamId: strAny(params, 'workstream_id', 'workstreamId'),
                        fields: {
                            title: str(params, 'title'),
                            description: str(params, 'description'),
                            producedOn: strAny(params, 'produced_on', 'producedOn'),
                            pwsRef: strAny(params, 'pws_ref', 'pwsRef'),
                            notInScopeDocs: bool(params, 'not_in_scope_docs'),
                            hours: num(params, 'hours'),
                            valueCents: optionalCents(params),
                            authorityBasis: authorityBasis(strAny(params, 'authority_basis', 'authorityBasis'))
                        },
                        attachments: wpFiles.map((f)=>({
                                artifactId: f.artifactId,
                                label: f.filename
                            }))
                    }
                }, opts);
                return {};
            }
        case 'update_work_product':
            {
                const wpFiles = await attachResolvedChatFiles(await resolveChatFiles(params), {
                    programId
                });
                const replaceAttachments = wpFiles.length > 0;
                await contractModelClient.updateWorkProduct({
                    body: {
                        ...bodyBase,
                        workProductId: strAny(params, 'work_product_id', 'workProductId'),
                        fields: {
                            title: str(params, 'title'),
                            description: str(params, 'description'),
                            producedOn: strAny(params, 'produced_on', 'producedOn'),
                            pwsRef: strAny(params, 'pws_ref', 'pwsRef'),
                            notInScopeDocs: bool(params, 'not_in_scope_docs'),
                            hours: num(params, 'hours'),
                            valueCents: optionalCents(params),
                            authorityBasis: authorityBasis(strAny(params, 'authority_basis', 'authorityBasis'))
                        },
                        replaceAttachments,
                        attachments: replaceAttachments ? wpFiles.map((f)=>({
                                artifactId: f.artifactId,
                                label: f.filename
                            })) : []
                    }
                }, opts);
                return {};
            }
        case 'assess_work_products':
            await contractModelClient.assessWorkProducts({
                body: {
                    ...bodyBase,
                    workProductIds: strList(params, 'work_product_ids'),
                    scopeAssessment: scopeAssessment(strAny(params, 'scope_assessment', 'scopeAssessment')),
                    commercialTreatment: commercialTreatment(strAny(params, 'commercial_treatment', 'commercialTreatment'))
                }
            }, opts);
            return {};
        case 'promote_work_product':
            await contractModelClient.promoteWorkProduct({
                body: {
                    ...bodyBase,
                    workProductId: strAny(params, 'work_product_id', 'workProductId'),
                    title: str(params, 'title')
                }
            }, opts);
            return {};
        case 'start_closeout_checklist':
            await deliveryClient.startCloseoutChecklist({
                programId,
                templateKey: strAny(params, 'template_key', 'templateKey')
            }, opts);
            return {};
        case 'answer_closeout_checklist_item':
            await deliveryClient.answerCloseoutChecklistItem({
                programId,
                checklistId: strAny(params, 'checklist_id', 'checklistId'),
                ordinal: Math.round(num(params, 'ordinal') ?? 0),
                answer: str(params, 'answer'),
                comments: strAny(params, 'comments', 'comment', 'note')
            }, opts);
            return {};
        case 'upsert_closeout_outstanding_item':
            await deliveryClient.upsertCloseoutOutstandingItem({
                programId,
                outstandingItem: {
                    id: str(params, 'id'),
                    checklistId: strAny(params, 'checklist_id', 'checklistId'),
                    item: str(params, 'item'),
                    plannedCompletionDate: ymdTimestamp(strAny(params, 'planned_completion_date', 'plannedCompletionDate')),
                    comments: strAny(params, 'comments', 'comment', 'note'),
                    responsibleParty: strAny(params, 'responsible_party', 'responsibleParty'),
                    resolved: bool(params, 'resolved')
                }
            }, opts);
            return {};
        case 'delete_closeout_outstanding_item':
            await deliveryClient.deleteCloseoutOutstandingItem({
                programId,
                id: strAny(params, 'outstanding_item_id', 'outstandingItemId')
            }, opts);
            return {};
        case 'complete_closeout_checklist':
            await deliveryClient.completeCloseoutChecklist({
                programId
            }, opts);
            return {};
        case 'upsert_program_capability':
            await deliveryClient.upsertProgramCapability({
                id: str(params, 'id'),
                programId,
                category: str(params, 'category'),
                percentage: num(params, 'percentage') ?? 0,
                description: str(params, 'description')
            }, opts);
            return {};
        case 'delete_program_capability':
            await deliveryClient.deleteProgramCapability({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'upsert_risk':
            await deliveryClient.upsertRisk({
                id: str(params, 'id'),
                programId,
                shortDescription: strAny(params, 'short_description', 'shortDescription'),
                category: str(params, 'category'),
                status: str(params, 'status'),
                strategy: str(params, 'strategy'),
                probability: str(params, 'probability'),
                impact: str(params, 'impact'),
                strategyPlan: strAny(params, 'strategy_plan', 'strategyPlan'),
                owner: str(params, 'owner'),
                targetResolutionDate: ymdTimestamp(strAny(params, 'target_resolution_date', 'targetResolutionDate')),
                comments: str(params, 'comments'),
                linkedDutyId: strAny(params, 'linked_duty_id', 'linkedDutyId') || undefined,
                linkedOccurrenceId: strAny(params, 'linked_occurrence_id', 'linkedOccurrenceId') || undefined
            }, opts);
            return {};
        case 'upsert_issue':
            await deliveryClient.upsertIssue({
                id: str(params, 'id'),
                programId,
                description: str(params, 'description'),
                priority: str(params, 'priority'),
                impact: str(params, 'impact'),
                state: str(params, 'state'),
                assignedTo: strAny(params, 'assigned_to', 'assignedTo'),
                rootCause: strAny(params, 'root_cause', 'rootCause'),
                targetResolutionDate: ymdTimestamp(strAny(params, 'target_resolution_date', 'targetResolutionDate')),
                highRiskFlag: bool(params, 'high_risk_flag'),
                linkedDutyId: strAny(params, 'linked_duty_id', 'linkedDutyId') || undefined,
                linkedOccurrenceId: strAny(params, 'linked_occurrence_id', 'linkedOccurrenceId') || undefined
            }, opts);
            return {};
        case 'convert_risk_to_issue':
            await deliveryClient.convertRiskToIssue({
                riskId: strAny(params, 'risk_id', 'riskId')
            }, opts);
            return {};
        case 'delete_risk':
            await deliveryClient.deleteRisk({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'delete_issue':
            await deliveryClient.deleteIssue({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'escalate_risk_to_leadership':
            await deliveryClient.escalateRiskToLeadership({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'extract_cpars_score':
            {
                // Step 1: Accept runs extraction only — no score write, no promote.
                const cparsFiles = await resolveChatFiles(params, {
                    pdfOnly: true
                });
                if (!cparsFiles[0]) {
                    throw new Error('provide a chat PDF (files from list-files)');
                }
                const extracted = await extractQualityScoreParams({
                    programId,
                    kind: 'cpars',
                    artifactId: cparsFiles[0].chatArtifactId
                });
                return applyResultStrings({
                    ...extracted,
                    files: cparsFiles[0].chatArtifactId,
                    file_names: cparsFiles[0].filename
                });
            }
        case 'upsert_cpars_score':
            {
                if (bool(params, 'extract')) {
                    throw new Error('Use extract_cpars_score to extract, then propose upsert_cpars_score with the extracted fields');
                }
                if (!strAny(params, 'eval_period_start', 'evalPeriodStart') || !strAny(params, 'eval_period_end', 'evalPeriodEnd')) {
                    throw new Error('evaluation period start and evaluation period end are required');
                }
                const cparsValidated = await resolveChatFiles(params, {
                    pdfOnly: true
                });
                const cparsFiles = await attachResolvedChatFiles(cparsValidated, {
                    programId
                });
                // Promoted copy when files were provided; otherwise the stored program
                // attachment_id enrich filled in (never a chat upload id).
                const attachmentId = cparsFiles[0]?.artifactId || str(params, 'attachment_id') || '';
                await deliveryClient.upsertCPARSScore({
                    id: str(params, 'id'),
                    programId,
                    overallScore: num(params, 'overall_score'),
                    evalPeriodStart: ymdTimestamp(strAny(params, 'eval_period_start', 'evalPeriodStart')),
                    evalPeriodEnd: ymdTimestamp(strAny(params, 'eval_period_end', 'evalPeriodEnd')),
                    overallComment: strAny(params, 'overall_comment', 'overallComment'),
                    qualityScore: num(params, 'quality_score'),
                    scheduleScore: num(params, 'schedule_score'),
                    costControlScore: num(params, 'cost_control_score'),
                    managementScore: num(params, 'management_score'),
                    smallBusinessScore: num(params, 'small_business_score'),
                    regulatoryComplianceScore: num(params, 'regulatory_compliance_score'),
                    qualityNarrative: strAny(params, 'quality_narrative', 'qualityNarrative'),
                    scheduleNarrative: strAny(params, 'schedule_narrative', 'scheduleNarrative'),
                    costControlNarrative: strAny(params, 'cost_control_narrative', 'costControlNarrative'),
                    managementNarrative: strAny(params, 'management_narrative', 'managementNarrative'),
                    smallBusinessNarrative: strAny(params, 'small_business_narrative', 'smallBusinessNarrative'),
                    regulatoryComplianceNarrative: strAny(params, 'regulatory_compliance_narrative', 'regulatoryComplianceNarrative'),
                    getWellPlan: strAny(params, 'get_well_plan', 'getWellPlan'),
                    attachmentId,
                    concurrenceStatus: strAny(params, 'concurrence_status', 'concurrenceStatus'),
                    concurrenceComment: strAny(params, 'concurrence_comment', 'concurrenceComment')
                }, opts);
                return attachmentId ? {
                    attachment_id: attachmentId
                } : {};
            }
        case 'delete_cpars_score':
            await deliveryClient.deleteCPARSScore({
                programId,
                id: str(params, 'id')
            }, opts);
            return {};
        case 'extract_qasp_score':
            {
                const qaspFiles = await resolveChatFiles(params, {
                    pdfOnly: true
                });
                if (!qaspFiles[0]) {
                    throw new Error('provide a chat PDF (files from list-files)');
                }
                const extracted = await extractQualityScoreParams({
                    programId,
                    kind: 'qasp',
                    artifactId: qaspFiles[0].chatArtifactId
                });
                return applyResultStrings({
                    ...extracted,
                    files: qaspFiles[0].chatArtifactId,
                    file_names: qaspFiles[0].filename
                });
            }
        case 'upsert_qasp_score':
            {
                if (bool(params, 'extract')) {
                    throw new Error('Use extract_qasp_score to extract, then propose upsert_qasp_score with the extracted fields');
                }
                if (!str(params, 'quarter') || !strAny(params, 'report_period_start', 'reportPeriodStart') || !strAny(params, 'report_period_end', 'reportPeriodEnd')) {
                    throw new Error('quarter, report period start, and report period end are required');
                }
                const qaspValidated = await resolveChatFiles(params, {
                    pdfOnly: true
                });
                const qaspFiles = await attachResolvedChatFiles(qaspValidated, {
                    programId
                });
                const attachmentId = qaspFiles[0]?.artifactId || str(params, 'attachment_id') || '';
                await deliveryClient.upsertQASPScore({
                    id: str(params, 'id'),
                    programId,
                    quarter: str(params, 'quarter'),
                    overallScore: num(params, 'overall_score'),
                    technicalScore: num(params, 'technical_score'),
                    scheduleScore: num(params, 'schedule_score'),
                    costStaffingScore: num(params, 'cost_staffing_score'),
                    managementScore: num(params, 'management_score'),
                    technicalNarrative: strAny(params, 'technical_narrative', 'technicalNarrative'),
                    scheduleNarrative: strAny(params, 'schedule_narrative', 'scheduleNarrative'),
                    costStaffingNarrative: strAny(params, 'cost_staffing_narrative', 'costStaffingNarrative'),
                    managementNarrative: strAny(params, 'management_narrative', 'managementNarrative'),
                    reportPeriodStart: ymdTimestamp(strAny(params, 'report_period_start', 'reportPeriodStart')),
                    reportPeriodEnd: ymdTimestamp(strAny(params, 'report_period_end', 'reportPeriodEnd')),
                    getWellPlan: strAny(params, 'get_well_plan', 'getWellPlan'),
                    attachmentId
                }, opts);
                return attachmentId ? {
                    attachment_id: attachmentId
                } : {};
            }
        case 'delete_qasp_score':
            await deliveryClient.deleteQASPScore({
                programId,
                id: str(params, 'id')
            }, opts);
            return {};
        case 'create_audit':
            {
                const resp = await deliveryClient.createAudit({
                    programId,
                    auditDate: ymdTimestamp(strAny(params, 'audit_date', 'auditDate')),
                    periodStart: ymdTimestamp(strAny(params, 'period_start', 'periodStart')),
                    periodEnd: ymdTimestamp(strAny(params, 'period_end', 'periodEnd')),
                    area: str(params, 'area'),
                    assignedTo: strAny(params, 'assigned_to', 'assignedTo'),
                    isFinal: bool(params, 'is_final')
                }, opts);
                const id = resp.audit?.id ?? '';
                const periodMonth = resp.audit?.periodMonth || periodMonthFromTimestamp(resp.audit?.auditDate) || '';
                return id ? {
                    id,
                    audit_id: id,
                    auditId: id,
                    ...periodMonth ? {
                        period_month: periodMonth,
                        periodMonth
                    } : {}
                } : {};
            }
        case 'update_audit':
            await deliveryClient.updateAudit({
                id: strAny(params, 'audit_id', 'auditId'),
                assignedTo: strAny(params, 'assigned_to', 'assignedTo'),
                notes: strAny(params, 'notes', 'note')
            }, opts);
            return {};
        case 'start_audit':
            {
                const auditId = strAny(params, 'audit_id', 'auditId', 'id');
                let periodMonth = strAny(params, 'period_month', 'periodMonth');
                if (auditId) {
                    const existing = await deliveryClient.getAudit({
                        id: auditId
                    }, opts);
                    const a = existing.audit;
                    if (a) {
                        const startedStates = new Set([
                            'In Progress',
                            'Corrective Actions Open',
                            'Complete',
                            'In Review',
                            'Published',
                            'Closed Complete'
                        ]);
                        if (a.state && startedStates.has(a.state)) {
                            return {
                                id: a.id,
                                audit_id: a.id,
                                auditId: a.id
                            };
                        }
                        if (!periodMonth) {
                            periodMonth = a.periodMonth || periodMonthFromTimestamp(a.auditDate) || '';
                        }
                    }
                }
                const resp = await deliveryClient.startAudit({
                    programId,
                    periodMonth
                }, opts);
                const id = resp.audit?.id ?? '';
                return id ? {
                    id,
                    audit_id: id,
                    auditId: id
                } : {};
            }
        case 'submit_audit_for_review':
            await deliveryClient.submitAuditForReview({
                auditId: strAny(params, 'audit_id', 'auditId')
            }, opts);
            return {};
        case 'answer_audit_question':
            await deliveryClient.upsertAuditQuestion({
                id: str(params, 'id'),
                auditId: strAny(params, 'audit_id', 'auditId'),
                question: str(params, 'question'),
                result: normalizeAuditResult(str(params, 'result')),
                notes: strAny(params, 'notes', 'note'),
                correctiveActionRequired: bool(params, 'corrective_action_required'),
                resolvedDuringAudit: bool(params, 'resolved_during_audit'),
                sortOrder: Math.round(num(params, 'sort_order') ?? 0)
            }, opts);
            return {};
        case 'complete_audit':
            await deliveryClient.completeAudit({
                auditId: strAny(params, 'audit_id', 'auditId'),
                auditSummary: strAny(params, 'audit_summary', 'auditSummary')
            }, opts);
            return {};
        case 'review_audit':
            await deliveryClient.reviewAudit({
                auditId: strAny(params, 'audit_id', 'auditId'),
                reviewerName: strAny(params, 'reviewer_name', 'reviewerName'),
                approved: bool(params, 'approved'),
                reviewerNotes: strAny(params, 'reviewer_notes', 'reviewerNotes')
            }, opts);
            return {};
        case 'close_audit':
            await deliveryClient.closeAudit({
                auditId: strAny(params, 'audit_id', 'auditId')
            }, opts);
            return {};
        case 'upsert_corrective_action':
            {
                const resp = await deliveryClient.upsertCorrectiveAction({
                    id: str(params, 'id'),
                    auditId: strAny(params, 'audit_id', 'auditId'),
                    questionId: strAny(params, 'question_id', 'questionId'),
                    description: str(params, 'description'),
                    assignedTo: strAny(params, 'assigned_to', 'assignedTo'),
                    assignedContactId: strAny(params, 'assigned_contact_id', 'assignedContactId'),
                    state: str(params, 'state'),
                    dueDate: ymdTimestamp(strAny(params, 'due_date', 'dueDate'))
                }, opts);
                const id = resp.action?.id ?? '';
                return id ? {
                    id,
                    corrective_action_id: id,
                    correctiveActionId: id
                } : {};
            }
        case 'request_ca_extension':
            await deliveryClient.requestCAExtension({
                correctiveActionId: strAny(params, 'corrective_action_id', 'correctiveActionId'),
                expectedDate: ymdTimestamp(strAny(params, 'expected_date', 'expectedDate')),
                reason: str(params, 'reason')
            }, opts);
            return {};
        case 'resolve_corrective_action':
            await deliveryClient.resolveCorrectiveAction({
                correctiveActionId: strAny(params, 'corrective_action_id', 'correctiveActionId'),
                resolutionNote: strAny(params, 'resolution_note', 'resolutionNote')
            }, opts);
            return {};
        case 'verify_corrective_action':
            await deliveryClient.verifyCorrectiveAction({
                correctiveActionId: strAny(params, 'corrective_action_id', 'correctiveActionId'),
                accepted: bool(params, 'accepted'),
                verifierNotes: strAny(params, 'verifier_notes', 'verifierNotes')
            }, opts);
            return {};
        case 'withdraw_corrective_action':
            await deliveryClient.withdrawCorrectiveAction({
                correctiveActionId: strAny(params, 'corrective_action_id', 'correctiveActionId'),
                reason: str(params, 'reason')
            }, opts);
            return {};
        case 'upsert_deviation':
            {
                const devParams = {
                    ...params
                };
                const devValidated = await resolveChatFiles(params);
                // Intelligence can read the chat upload (owner Accept); promote before link.
                if (bool(devParams, 'process_approval_evidence') && devValidated[0]) {
                    const { IntelligenceTypes } = await import('@mono/proto-clients');
                    const { intelligenceClient } = await import('../../../lib/connect/clients');
                    const created = await intelligenceClient.createIntelligenceRun({
                        workflowKind: IntelligenceTypes.WorkflowKind.DEVIATION,
                        target: {
                            programId,
                            entityType: 'Deviation',
                            entityId: str(devParams, 'id')
                        },
                        sourceArtifactIds: [
                            devValidated[0].chatArtifactId
                        ]
                    });
                    const runId = created.run?.id ?? '';
                    if (runId) {
                        await intelligenceClient.startIntelligenceRun({
                            runId
                        });
                    }
                // Extraction proposals land in Quality UI for review; Accept still upserts
                // the deviation row and links evidence below (same as DeviationsTab Save).
                }
                const devFiles = await attachResolvedChatFiles(devValidated, {
                    programId
                });
                const resp = await deliveryClient.upsertDeviation({
                    id: str(devParams, 'id'),
                    programId,
                    type: str(devParams, 'type').toUpperCase(),
                    category: str(devParams, 'category'),
                    comment: strAny(devParams, 'comment', 'notes', 'note'),
                    state: str(devParams, 'state'),
                    approvalSource: strAny(devParams, 'approval_source', 'approvalSource'),
                    linkedTaskId: '',
                    linkedClinId: '',
                    approvedByContactId: strAny(devParams, 'approved_by_contact_id', 'approvedByContactId'),
                    approvedByContactName: strAny(devParams, 'approved_by_contact_name', 'approvedByContactName'),
                    approvedReplacementDueDate: ymdTimestamp(strAny(devParams, 'approved_replacement_due_date', 'approvedReplacementDueDate')),
                    linkedDutyId: strAny(devParams, 'linked_duty_id', 'linkedDutyId') || undefined,
                    linkedOccurrenceId: strAny(devParams, 'linked_occurrence_id', 'linkedOccurrenceId') || undefined,
                    approvedCadence: strAny(devParams, 'approved_cadence', 'approvedCadence') || undefined
                }, opts);
                const deviationId = resp.deviation?.id || str(devParams, 'id');
                if (deviationId && devFiles.length > 0) {
                    for (const f of devFiles){
                        await artifactClient.linkArtifactToEntity({
                            artifactId: f.artifactId,
                            entityType: 'deviation',
                            entityId: deviationId
                        });
                    }
                }
                return deviationId ? {
                    id: deviationId
                } : {};
            }
        case 'delete_deviation':
            await deliveryClient.deleteDeviation({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'create_contact':
            {
                const firstName = strAny(params, 'first_name', 'firstName');
                const lastName = strAny(params, 'last_name', 'lastName');
                const email = str(params, 'email');
                const corporateEmail = strAny(params, 'corporate_email', 'corporateEmail');
                const governmentEmail = strAny(params, 'government_email', 'governmentEmail');
                const roleTypeId = strAny(params, 'role_type_id', 'roleTypeId');
                const resp = await deliveryClient.upsertContact({
                    id: '',
                    companyId: strAny(params, 'company_id', 'companyId'),
                    firstName,
                    lastName,
                    title: str(params, 'title'),
                    email,
                    phone: str(params, 'phone'),
                    location: str(params, 'location'),
                    organization: str(params, 'organization'),
                    contactType: strAny(params, 'contact_type', 'contactType') || 'INTERNAL',
                    isActive: true,
                    ...corporateEmail ? {
                        corporateEmail
                    } : {},
                    ...governmentEmail ? {
                        governmentEmail
                    } : {},
                    ...roleTypeId ? {
                        roleTypeId
                    } : {}
                }, opts);
                const id = resp.contact?.id ?? '';
                return id ? {
                    id,
                    contact_id: id,
                    contactId: id
                } : {};
            }
        case 'upsert_program_contact':
            await deliveryClient.upsertProgramContact({
                id: str(params, 'id'),
                programId,
                contactId: strAny(params, 'contact_id', 'contactId'),
                role: str(params, 'role'),
                customerPocRoleId: strAny(params, 'customer_poc_role_id', 'customerPocRoleId')
            }, opts);
            return {};
        case 'delete_program_contact':
            await deliveryClient.deleteProgramContact({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'upsert_program_role_assignment':
            await deliveryClient.upsertProgramRoleAssignment({
                id: str(params, 'id'),
                programId,
                contactId: strAny(params, 'contact_id', 'contactId'),
                userId: strAny(params, 'user_id', 'userId'),
                roleCategory: 'INTERNAL',
                roleTypeId: strAny(params, 'role_type_id', 'roleTypeId'),
                isPrimary: bool(params, 'is_primary')
            }, opts);
            return {};
        case 'delete_program_role_assignment':
            await deliveryClient.deleteProgramRoleAssignment({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'merge_contacts':
            await deliveryClient.mergeContacts({
                companyId: strAny(params, 'company_id', 'companyId'),
                loserContactId: strAny(params, 'loser_contact_id', 'loserContactId'),
                survivorContactId: strAny(params, 'survivor_contact_id', 'survivorContactId')
            }, opts);
            return {};
        case 'upsert_program_financial_month':
            await deliveryClient.upsertProgramFinancialMonth({
                programId,
                periodMonth: strAny(params, 'period_month', 'periodMonth'),
                burnRateCents: BigInt(Math.round(num(params, 'burn_rate_cents') ?? 0)),
                arBalanceCents: BigInt(Math.round(num(params, 'ar_balance_cents') ?? 0)),
                comment: str(params, 'comment')
            }, opts);
            return {};
        case 'set_funding_alert_threshold':
            await deliveryClient.setProgramFundingAlertThreshold({
                programId,
                thresholdPct: Math.round(num(params, 'threshold_pct') ?? 0)
            }, opts);
            return {};
        case 'upsert_program_company_staffing_line':
            await deliveryClient.upsertProgramCompanyStaffingLine({
                id: str(params, 'id'),
                programId,
                companyAssignmentId: strAny(params, 'company_assignment_id', 'companyAssignmentId'),
                lcatId: strAny(params, 'lcat_id', 'lcatId'),
                lcatTitle: strAny(params, 'lcat_title', 'lcatTitle'),
                positionTitle: strAny(params, 'position_title', 'positionTitle'),
                fte: num(params, 'fte') ?? 0,
                contactId: strAny(params, 'contact_id', 'contactId'),
                startDate: ymdTimestamp(strAny(params, 'start_date', 'startDate')),
                endDate: ymdTimestamp(strAny(params, 'end_date', 'endDate')),
                notes: str(params, 'notes')
            }, opts);
            return {};
        case 'delete_program_company_staffing_line':
            await deliveryClient.deleteProgramCompanyStaffingLine({
                id: str(params, 'id'),
                programId
            }, opts);
            return {};
        case 'log_contract_event':
            {
                const eventFiles = await attachResolvedChatFiles(await resolveChatFiles(params), {
                    programId
                });
                let sourceDocumentId = '';
                if (eventFiles[0]) {
                    sourceDocumentId = await createEvidenceProgramDocument(programId, eventFiles[0], {
                        headers: assistHeaders
                    });
                }
                await contractModelClient.logContractEvent({
                    body: {
                        ...bodyBase,
                        kind: str(params, 'kind'),
                        occurredOn: strAny(params, 'occurred_on', 'occurredOn'),
                        label: strAny(params, 'note', 'label'),
                        // SAD-963: the team the event dates; omitted = contract-wide.
                        workstreamId: strAny(params, 'workstream_id', 'workstreamId'),
                        ...sourceDocumentId ? {
                            sourceDocumentId
                        } : {}
                    }
                }, opts);
                return {};
            }
        case 'upsert_customer_touchpoint':
            {
                const tpValidated = await resolveChatFiles(params);
                const briefingRaw = strAny(params, 'date_of_briefing', 'dateOfBriefing');
                const briefingDate = parseLocalDate(briefingRaw) ?? new Date();
                const resp = await deliveryClient.upsertCustomerTouchpoint({
                    id: str(params, 'id'),
                    programId,
                    method: str(params, 'method'),
                    briefingLevel: '',
                    dateOfBriefing: Timestamp.fromDate(briefingDate),
                    briefDescription: strAny(params, 'brief_description', 'briefDescription'),
                    providedBy: '',
                    providedByContactId: '',
                    providedToContactId: '',
                    providedToSecondaryId: '',
                    providedToPrimary: '',
                    providedToSecondary: '',
                    comments: str(params, 'comments'),
                    summaryAndActionItems: strAny(params, 'summary_and_action_items', 'summaryAndActionItems'),
                    attendees: strList(params, 'attendees')
                }, opts);
                const touchpointId = resp.touchpoint?.id || str(params, 'id');
                if (touchpointId) {
                    const tpFiles = await attachResolvedChatFiles(tpValidated, {
                        programId
                    });
                    for (const f of tpFiles){
                        await deliveryClient.attachTouchpointFile({
                            programId,
                            touchpointId,
                            artifactId: f.artifactId
                        }, opts);
                    }
                    if (bool(params, 'process_notes') && tpFiles.length > 0) {
                        const { IntelligenceTypes } = await import('@mono/proto-clients');
                        const { intelligenceClient } = await import('../../../lib/connect/clients');
                        const nonAudio = tpFiles.filter((f)=>!f.mimeType.startsWith('audio/'));
                        if (nonAudio.length > 0) {
                            const created = await intelligenceClient.createIntelligenceRun({
                                workflowKind: IntelligenceTypes.WorkflowKind.TOUCHPOINT,
                                target: {
                                    programId,
                                    entityType: 'CustomerTouchpoint',
                                    entityId: touchpointId
                                },
                                sourceArtifactIds: nonAudio.map((f)=>f.artifactId)
                            });
                            const runId = created.run?.id ?? '';
                            if (runId) await intelligenceClient.startIntelligenceRun({
                                runId
                            });
                        }
                    }
                }
                return touchpointId ? {
                    id: touchpointId
                } : {};
            }
        case 'create_program_document':
            {
                const docValidated = await resolveChatFiles(params);
                if (docValidated.length === 0) {
                    throw new Error('provide files (chat attachment ids from list-files)');
                }
                const category = normalizeProgramDocumentCategory(str(params, 'category') || 'Other');
                const title = str(params, 'title') || docValidated[0].filename;
                const prog = await deliveryClient.getProgram({
                    id: programId
                }, opts);
                const zouProjectId = prog.program?.zouProjectId ?? '';
                const isSow = isSowDocumentCategory(category);
                if (isSow && !zouProjectId) {
                    throw new Error('This program has no contract project to start SOW intake.');
                }
                const isContractish = isContractDocumentCategory(category) || isAttachmentDocumentCategory(category) || isSow;
                const docFiles = await attachResolvedChatFiles(docValidated, {
                    programId,
                    projectId: zouProjectId || undefined
                });
                let fileId = docFiles[0].artifactId;
                if (isContractish && zouProjectId) {
                    const { projectApiConnect } = await import('../../../lib/api/projectApi.connect');
                    await projectApiConnect.attachArtifactsToProject(zouProjectId, docFiles.map((f)=>f.artifactId));
                    fileId = docFiles[0].artifactId;
                }
                const docDateRaw = strAny(params, 'doc_date', 'docDate');
                const docDay = docDateRaw ? parseLocalDate(docDateRaw) : new Date();
                const day = docDay ?? new Date();
                const resp = await deliveryClient.createProgramDocument({
                    programId,
                    title,
                    description: str(params, 'description'),
                    docDate: Timestamp.fromDate(new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()))),
                    fileId,
                    fileName: '',
                    category,
                    uploadedBy: '',
                    companyAssignmentId: strAny(params, 'company_assignment_id', 'companyAssignmentId'),
                    contactId: strAny(params, 'contact_id', 'contactId'),
                    issueId: strAny(params, 'issue_id', 'issueId')
                }, opts);
                if (isSow && zouProjectId) {
                    try {
                        await startSowIntake({
                            programId,
                            zouProjectId,
                            attachmentId: fileId,
                            workstreamId: strAny(params, 'workstream_id', 'workstreamId') || undefined
                        });
                    } catch (err) {
                        const id = resp.document?.id ?? '';
                        return {
                            ...id ? {
                                id
                            } : {},
                            href: sowReceiptHref(programId),
                            intake: err instanceof Error ? err.message : 'SOW intake failed to start'
                        };
                    }
                } else if (isContractish && zouProjectId) {
                    const { proposalProjectApiConnect } = await import('../../../lib/api/proposalProjectApi.connect');
                    const kindRaw = strAny(params, 'document_kind', 'documentKind');
                    const documentKind = kindRaw === 'attachment' || isAttachmentDocumentCategory(category) ? 'attachment' : 'base';
                    await proposalProjectApiConnect.processBatch(zouProjectId, [
                        fileId
                    ], {
                        documentKind
                    });
                }
                const id = resp.document?.id ?? '';
                const href = isSow ? sowReceiptHref(programId) : receiptHrefForAction(row.action, {
                    programId,
                    category,
                    docId: id
                });
                return id ? {
                    id,
                    href
                } : href ? {
                    href
                } : {};
            }
        case 'upload_modification':
            {
                const modValidated = await resolveChatFiles(params, {
                    pdfOnly: true
                });
                if (modValidated.length === 0) {
                    throw new Error('provide files (chat PDF ids from list-files)');
                }
                const prog = await deliveryClient.getProgram({
                    id: programId
                }, opts);
                const zouProjectId = prog.program?.zouProjectId ?? '';
                if (!zouProjectId) {
                    throw new Error('This program has no contract project to upload a modification against.');
                }
                const modFiles = await attachResolvedChatFiles(modValidated, {
                    programId,
                    projectId: zouProjectId
                });
                const { projectApiConnect } = await import('../../../lib/api/projectApi.connect');
                const { proposalProjectApiConnect } = await import('../../../lib/api/proposalProjectApi.connect');
                const ids = modFiles.map((f)=>f.artifactId);
                await projectApiConnect.attachArtifactsToProject(zouProjectId, ids);
                // Same as UploadModificationButton: processBatch with no documentKind
                // (classify-by-content). Never commits — commit_modification stays never-tier.
                await proposalProjectApiConnect.processBatch(zouProjectId, ids);
                return {
                    href: receiptHrefForAction('upload_modification', {
                        programId
                    })
                };
            }
        // --- SAD-941 PR4: deliverables / schedule / submissions / SOW ---
        case 'create_deliverable':
            {
                const resp = await contractModelClient.createDuty({
                    body: {
                        ...bodyBase,
                        dutyKey: strAny(params, 'duty_key', 'dutyKey'),
                        origin: str(params, 'origin') || 'manual',
                        sowId: strAny(params, 'sow_id', 'sowId'),
                        fields: dutyFieldsFromParams(params)
                    }
                }, opts);
                const id = resp.body?.duty?.id ?? '';
                return id ? {
                    id,
                    duty_id: id,
                    dutyId: id
                } : {};
            }
        case 'update_deliverable':
            await contractModelClient.updateDuty({
                body: {
                    ...bodyBase,
                    dutyId: strAny(params, 'duty_id', 'dutyId'),
                    fields: dutyFieldsFromParams(params)
                }
            }, opts);
            return {};
        case 'delete_deliverable':
            await contractModelClient.deleteDuty({
                body: {
                    ...bodyBase,
                    dutyId: strAny(params, 'duty_id', 'dutyId')
                }
            }, opts);
            return {};
        case 'create_schedule_rule':
            {
                const resp = await contractModelClient.createScheduleRule({
                    body: {
                        ...bodyBase,
                        dutyId: strAny(params, 'duty_id', 'dutyId'),
                        fields: scheduleFieldsFromParams(params)
                    }
                }, opts);
                const id = resp.body?.rule?.id ?? '';
                return id ? {
                    id,
                    rule_id: id,
                    ruleId: id
                } : {};
            }
        case 'update_schedule_rule':
            await contractModelClient.updateScheduleRule({
                body: {
                    ...bodyBase,
                    ruleId: strAny(params, 'rule_id', 'ruleId'),
                    fields: scheduleFieldsFromParams(params)
                }
            }, opts);
            return {};
        case 'delete_schedule_rule':
            await contractModelClient.deleteScheduleRule({
                body: {
                    ...bodyBase,
                    ruleId: strAny(params, 'rule_id', 'ruleId')
                }
            }, opts);
            return {};
        case 'assign_deliverable_owners':
            {
                const teamRulesRaw = params.team_rules ?? params.teamRules;
                await contractModelClient.setDutyAssignment({
                    body: {
                        ...bodyBase,
                        sowId: strAny(params, 'sow_id', 'sowId'),
                        dutyId: strAny(params, 'duty_id', 'dutyId'),
                        handling: str(params, 'handling'),
                        routedToWorkstreamId: strAny(params, 'routed_to_workstream_id', 'routedToWorkstreamId'),
                        notOwedReason: strAny(params, 'not_owed_reason', 'notOwedReason'),
                        titleAsStated: strAny(params, 'title_as_stated', 'titleAsStated'),
                        recipient: str(params, 'recipient'),
                        format: str(params, 'format'),
                        submissionMethod: strAny(params, 'submission_method', 'submissionMethod'),
                        acceptanceCriteria: strAny(params, 'acceptance_criteria', 'acceptanceCriteria'),
                        senderContactId: strAny(params, 'sender_contact_id', 'senderContactId'),
                        sourceLocator: strAny(params, 'source_locator', 'sourceLocator'),
                        note: str(params, 'note'),
                        ...Array.isArray(teamRulesRaw) ? {
                            teamRules: teamRulesRaw
                        } : {}
                    }
                }, opts);
                return {};
            }
        case 'update_deliverable_settings':
            {
                const settingsBody = {
                    ...bodyBase
                };
                if (hasParam(params, 'roll')) settingsBody.roll = str(params, 'roll');
                if (hasParam(params, 'roll_locator', 'rollLocator')) {
                    settingsBody.rollLocator = strAny(params, 'roll_locator', 'rollLocator');
                }
                if (bool(params, 'replace_recipients') || bool(params, 'replaceRecipients')) {
                    settingsBody.replaceRecipients = true;
                    settingsBody.recipients = strList(params, 'recipients');
                }
                if (hasParam(params, 'set_higher_tier_satisfies', 'setHigherTierSatisfies', 'higher_tier_satisfies', 'higherTierSatisfies')) {
                    settingsBody.higherTierSatisfies = bool(params, 'higher_tier_satisfies') || bool(params, 'higherTierSatisfies');
                }
                await contractModelClient.updateDeliverableSettings({
                    body: settingsBody
                }, opts);
                return {};
            }
        case 'propose_submission_edit':
            {
                const attachments = await resolveSubmissionAttachments(params, programId, options?.fileCache);
                await contractModelClient.proposeSubmissionEdit({
                    body: {
                        ...bodyBase,
                        occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                        submissionId: strAny(params, 'submission_id', 'submissionId'),
                        submittedOn: strAny(params, 'submitted_on', 'submittedOn'),
                        submittedByContactId: strAny(params, 'submitted_by_contact_id', 'submittedByContactId'),
                        submittedByDisplay: strAny(params, 'submitted_by_display', 'submittedByDisplay'),
                        sentToContactIds: strList(params, 'sent_to_contact_ids').length ? strList(params, 'sent_to_contact_ids') : strList(params, 'sentToContactIds'),
                        submissionNote: strAny(params, 'submission_note', 'submissionNote'),
                        note: strAny(params, 'note', 'reason', 'message'),
                        replaceAttachments: bool(params, 'replace_attachments') || bool(params, 'replaceAttachments'),
                        attachments
                    }
                }, opts);
                return {};
            }
        case 'propose_waiver_evidence':
            {
                const attachments = await resolveSubmissionAttachments(params, programId, options?.fileCache);
                if (attachments.length === 0) {
                    throw new Error('provide files (chat attachment ids from list-files) and/or link_url');
                }
                await contractModelClient.proposeWaiverEvidence({
                    body: {
                        ...bodyBase,
                        occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                        note: strAny(params, 'note', 'reason', 'message'),
                        attachments
                    }
                }, opts);
                return {};
            }
        case 'propose_waiver_edit':
            {
                // Only the fields the row names are sent: an unset field keeps the
                // stored waiver value (proto3 optional).
                const changes = {};
                if ('waived_on' in params) changes.waivedOn = str(params, 'waived_on');
                if ('reason' in params) changes.reason = str(params, 'reason');
                if ('waiver_document_id' in params) {
                    changes.waiverDocumentId = str(params, 'waiver_document_id');
                }
                await contractModelClient.proposeWaiverEdit({
                    body: {
                        ...bodyBase,
                        occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                        note: strAny(params, 'note', 'message'),
                        ...changes
                    }
                }, opts);
                return {};
            }
        case 'propose_submission_evidence':
            {
                const attachments = await resolveSubmissionAttachments(params, programId, options?.fileCache);
                if (attachments.length === 0) {
                    throw new Error('provide files (chat attachment ids from list-files) and/or link_url');
                }
                await contractModelClient.proposeSubmissionEvidence({
                    body: {
                        ...bodyBase,
                        occurrenceId: strAny(params, 'occurrence_id', 'occurrenceId'),
                        submissionId: strAny(params, 'submission_id', 'submissionId'),
                        note: strAny(params, 'note', 'reason', 'message'),
                        attachments
                    }
                }, opts);
                return {};
            }
        case 'create_sow':
            {
                const resp = await contractModelClient.createSow({
                    body: {
                        ...bodyBase,
                        workstreamId: strAny(params, 'workstream_id', 'workstreamId'),
                        title: str(params, 'title'),
                        version: str(params, 'version'),
                        approvedOn: strAny(params, 'approved_on', 'approvedOn'),
                        popStart: strAny(params, 'pop_start', 'popStart'),
                        popEnd: strAny(params, 'pop_end', 'popEnd'),
                        programDocumentId: strAny(params, 'program_document_id', 'programDocumentId'),
                        notHeld: bool(params, 'not_held') || bool(params, 'notHeld'),
                        namedInSowId: strAny(params, 'named_in_sow_id', 'namedInSowId'),
                        namedInLocator: strAny(params, 'named_in_locator', 'namedInLocator'),
                        namedInQuote: strAny(params, 'named_in_quote', 'namedInQuote'),
                        supersedesSowId: strAny(params, 'supersedes_sow_id', 'supersedesSowId'),
                        asOf: strAny(params, 'as_of', 'asOf')
                    }
                }, opts);
                const id = resp.body?.sow?.id ?? '';
                return id ? {
                    id,
                    sow_id: id,
                    sowId: id
                } : {};
            }
        case 'rule_sow_gap':
            await contractModelClient.ruleSowGap({
                body: {
                    ...bodyBase,
                    workstreamId: strAny(params, 'workstream_id', 'workstreamId'),
                    fromOn: strAny(params, 'from_on', 'fromOn'),
                    toOn: strAny(params, 'to_on', 'toOn'),
                    choice: sowGapChoice(params.choice),
                    sowId: strAny(params, 'sow_id', 'sowId'),
                    changeItemId: strAny(params, 'change_item_id', 'changeItemId')
                }
            }, opts);
            return {};
        case 'set_exercise_team':
            await contractModelClient.setExerciseWorkstream({
                body: {
                    ...bodyBase,
                    exerciseId: strAny(params, 'exercise_id', 'exerciseId'),
                    workstreamIds: strList(params, 'workstream_ids').length ? strList(params, 'workstream_ids') : strList(params, 'workstreamIds'),
                    clinNumber: strAny(params, 'clin_number', 'clinNumber'),
                    sowId: strAny(params, 'sow_id', 'sowId'),
                    sourceLocator: strAny(params, 'source_locator', 'sourceLocator')
                }
            }, opts);
            return {};
        case 'edit_comment':
            await contractModelClient.updateComment({
                body: {
                    ...bodyBase,
                    commentId: strAny(params, 'comment_id', 'commentId'),
                    body: str(params, 'body')
                }
            }, opts);
            return {};
        // --- SAD-941 PR4: award discrepancies / extraction ---
        case 'resolve_discrepancy':
            await projectDataClient.resolveDiscrepancy({
                discrepancyId: strAny(params, 'discrepancy_id', 'discrepancyId'),
                resolutionState: strAny(params, 'resolution_state', 'resolutionState'),
                decisionRationale: strAny(params, 'decision_rationale', 'decisionRationale'),
                decidedValueJson: strAny(params, 'decided_value_json', 'decidedValueJson')
            });
            return {};
        case 'route_discrepancy':
            await projectDataClient.routeDiscrepancy({
                discrepancyId: strAny(params, 'discrepancy_id', 'discrepancyId'),
                functionalOwner: strAny(params, 'functional_owner', 'functionalOwner')
            });
            return {};
        case 'set_discrepancy_target_period':
            {
                const periodId = strAny(params, 'period_id', 'periodId');
                const targetObjectId = strAny(params, 'target_object_id', 'targetObjectId');
                await projectDataClient.setDiscrepancyTargetPeriod({
                    discrepancyId: strAny(params, 'discrepancy_id', 'discrepancyId'),
                    periodId: periodId ? periodId : '',
                    targetObjectId: periodId ? '' : targetObjectId
                });
                return {};
            }
        case 'reextract_contract':
            {
                const kindRaw = strAny(params, 'document_kind', 'documentKind').toUpperCase();
                const documentKind = kindRaw === 'MODIFICATION' ? ProjectDataTypes.ReprocessDocumentKind.MODIFICATION : ProjectDataTypes.ReprocessDocumentKind.BASE;
                await projectDataClient.reprocessContractExtraction({
                    projectId: strAny(params, 'project_id', 'projectId'),
                    reextractDocId: strAny(params, 'reextract_doc_id', 'reextractDocId'),
                    documentKind,
                    programId: strAny(params, 'program_id', 'programId') || programId
                });
                return {};
            }
        case 'update_activation_gate_item':
            await projectDataClient.updateActivationGateItem({
                contractProjectId: strAny(params, 'contract_project_id', 'contractProjectId'),
                itemKey: strAny(params, 'item_key', 'itemKey'),
                state: str(params, 'state'),
                reason: str(params, 'reason')
            });
            return {};
        case 'review_extracted_field':
            await intelligenceClient.reviewFieldProposal({
                runId: strAny(params, 'run_id', 'runId'),
                proposalId: strAny(params, 'proposal_id', 'proposalId'),
                reviewState: proposalReviewState(params.review_state ?? params.reviewState),
                reviewedValue: reviewedValueFromParam(params.reviewed_value ?? params.reviewedValue)
            });
            return {};
        case 'bulk_review_extracted_fields':
            {
                const decisionsRaw = params.decisions;
                if (!Array.isArray(decisionsRaw) || decisionsRaw.length === 0) {
                    throw new Error('decisions must be a non-empty array');
                }
                await intelligenceClient.bulkReviewFieldProposals({
                    runId: strAny(params, 'run_id', 'runId'),
                    decisions: decisionsRaw.map((raw)=>{
                        const d = raw ?? {};
                        return {
                            proposalId: strAny(d, 'proposal_id', 'proposalId'),
                            reviewState: proposalReviewState(d.review_state ?? d.reviewState),
                            reviewedValue: reviewedValueFromParam(d.reviewed_value ?? d.reviewedValue)
                        };
                    })
                });
                return {};
            }
        case 'retry_extraction':
            {
                const runId = strAny(params, 'run_id', 'runId');
                await intelligenceClient.retryIntelligenceRun({
                    runId
                });
                await intelligenceClient.startIntelligenceRun({
                    runId
                });
                return {};
            }
        // --- SAD-941 PR4: documents / program setup / growth / subs ---
        case 'delete_program_document':
            await deliveryClient.deleteProgramDocument({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'update_program':
            {
                const patch = {};
                if (hasParam(params, 'program_name_full', 'programNameFull')) {
                    patch.programNameFull = strAny(params, 'program_name_full', 'programNameFull');
                }
                if (hasParam(params, 'program_name_short', 'programNameShort')) {
                    patch.programNameShort = strAny(params, 'program_name_short', 'programNameShort');
                }
                if (hasParam(params, 'contract_number', 'contractNumber')) {
                    patch.contractNumber = strAny(params, 'contract_number', 'contractNumber');
                }
                if (hasParam(params, 'contract_title', 'contractTitle')) {
                    patch.contractTitle = strAny(params, 'contract_title', 'contractTitle');
                }
                if (hasParam(params, 'pricing_type', 'pricingType')) {
                    patch.pricingType = strAny(params, 'pricing_type', 'pricingType');
                }
                if (hasParam(params, 'operational_state_id', 'operationalStateId')) {
                    patch.operationalStateId = strAny(params, 'operational_state_id', 'operationalStateId');
                }
                if (hasParam(params, 'record_status_id', 'recordStatusId')) {
                    patch.recordStatusId = strAny(params, 'record_status_id', 'recordStatusId');
                }
                if (hasParam(params, 'contract_lifecycle_state_id', 'contractLifecycleStateId')) {
                    patch.contractLifecycleStateId = strAny(params, 'contract_lifecycle_state_id', 'contractLifecycleStateId');
                }
                if (hasParam(params, 'delivery_model_type_id', 'deliveryModelTypeId')) {
                    patch.deliveryModelTypeId = strAny(params, 'delivery_model_type_id', 'deliveryModelTypeId');
                }
                if (hasParam(params, 'government_organization_id', 'governmentOrganizationId')) {
                    patch.governmentOrganizationId = strAny(params, 'government_organization_id', 'governmentOrganizationId');
                }
                if (hasParam(params, 'customer_org_id', 'customerOrgId')) {
                    patch.customerOrgId = strAny(params, 'customer_org_id', 'customerOrgId');
                }
                if (hasParam(params, 'company_role_type', 'companyRoleType')) {
                    patch.companyRoleType = strAny(params, 'company_role_type', 'companyRoleType');
                }
                if (hasParam(params, 'prime_contractor_company_id', 'primeContractorCompanyId')) {
                    patch.primeContractorCompanyId = strAny(params, 'prime_contractor_company_id', 'primeContractorCompanyId');
                }
                if (hasParam(params, 'sector_id', 'sectorId')) {
                    patch.sectorId = strAny(params, 'sector_id', 'sectorId');
                }
                if (hasParam(params, 'business_unit_id', 'businessUnitId')) {
                    patch.businessUnitId = strAny(params, 'business_unit_id', 'businessUnitId');
                }
                if (hasParam(params, 'portfolio_id', 'portfolioId')) {
                    patch.portfolioId = strAny(params, 'portfolio_id', 'portfolioId');
                }
                const prog = await deliveryClient.getProgram({
                    id: programId
                }, opts);
                if (!prog.program) {
                    throw new Error('Program not found');
                }
                const next = patchProgram(prog.program, patch);
                await deliveryClient.updateProgram({
                    id: programId,
                    program: next
                }, opts);
                return {};
            }
        case 'upsert_period_of_performance':
            await deliveryClient.upsertPeriodOfPerformance({
                id: str(params, 'id'),
                programId,
                periodName: strAny(params, 'period_name', 'periodName'),
                periodType: strAny(params, 'period_type', 'periodType'),
                optionNumber: numAny(params, 'option_number', 'optionNumber') ?? 0,
                exercised: bool(params, 'exercised'),
                sortOrder: numAny(params, 'sort_order', 'sortOrder') ?? 0,
                startDate: ymdUtcTimestamp(strAny(params, 'start_date', 'startDate')),
                endDate: ymdUtcTimestamp(strAny(params, 'end_date', 'endDate'))
            }, opts);
            return {};
        case 'delete_period_of_performance':
            await deliveryClient.deletePeriodOfPerformance({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'upsert_optional_task':
            await deliveryClient.upsertOptionalTask({
                id: str(params, 'id'),
                programId,
                name: str(params, 'name'),
                taskNumber: numAny(params, 'task_number', 'taskNumber') ?? 0,
                exercised: bool(params, 'exercised'),
                description: str(params, 'description'),
                fundedAmount: numAny(params, 'funded_amount', 'fundedAmount') ?? 0,
                totalAmount: numAny(params, 'total_amount', 'totalAmount') ?? 0,
                requirementText: strAny(params, 'requirement_text', 'requirementText'),
                status: str(params, 'status'),
                clinNumber: strAny(params, 'clin_number', 'clinNumber'),
                pricingType: strAny(params, 'pricing_type', 'pricingType'),
                pwsSections: strAny(params, 'pws_sections', 'pwsSections')
            }, opts);
            return {};
        case 'delete_optional_task':
            await deliveryClient.deleteOptionalTask({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'create_option_exercise':
            await deliveryClient.createOptionalTaskExercise({
                programId,
                optionalTaskId: strAny(params, 'optional_task_id', 'optionalTaskId'),
                quantity: num(params, 'quantity') ?? 0,
                cost: num(params, 'cost') ?? 0,
                unitPrice: numAny(params, 'unit_price', 'unitPrice') ?? 0,
                clinNumber: strAny(params, 'clin_number', 'clinNumber'),
                description: str(params, 'description'),
                exercisedAt: ymdUtcTimestamp(strAny(params, 'exercised_at', 'exercisedAt')),
                popStartDate: ymdUtcTimestamp(strAny(params, 'pop_start_date', 'popStartDate')),
                popEndDate: ymdUtcTimestamp(strAny(params, 'pop_end_date', 'popEndDate'))
            }, opts);
            return {};
        case 'update_option_exercise':
            await deliveryClient.updateOptionalTaskExercise({
                id: str(params, 'id'),
                programId,
                quantity: num(params, 'quantity') ?? 0,
                cost: num(params, 'cost') ?? 0,
                unitPrice: numAny(params, 'unit_price', 'unitPrice') ?? 0,
                clinNumber: strAny(params, 'clin_number', 'clinNumber'),
                description: str(params, 'description'),
                amountExercised: numAny(params, 'amount_exercised', 'amountExercised') ?? 0,
                applyPopToLines: bool(params, 'apply_pop_to_lines') || bool(params, 'applyPopToLines'),
                exercisedAt: ymdUtcTimestamp(strAny(params, 'exercised_at', 'exercisedAt')),
                popStartDate: ymdUtcTimestamp(strAny(params, 'pop_start_date', 'popStartDate')),
                popEndDate: ymdUtcTimestamp(strAny(params, 'pop_end_date', 'popEndDate'))
            }, opts);
            return {};
        case 'update_option_exercise_line':
            await deliveryClient.updateOptionalTaskExerciseLine({
                id: str(params, 'id'),
                exerciseId: strAny(params, 'exercise_id', 'exerciseId'),
                programId,
                popStartDate: ymdUtcTimestamp(strAny(params, 'pop_start_date', 'popStartDate')),
                popEndDate: ymdUtcTimestamp(strAny(params, 'pop_end_date', 'popEndDate'))
            }, opts);
            return {};
        case 'delete_option_exercise':
            await deliveryClient.deleteOptionalTaskExercise({
                id: str(params, 'id'),
                programId
            }, opts);
            return {};
        case 'upsert_program_company':
            await deliveryClient.upsertProgramCompanyAssignment({
                id: str(params, 'id'),
                programId,
                companyProfileId: strAny(params, 'company_profile_id', 'companyProfileId'),
                active: bool(params, 'active'),
                type: str(params, 'type'),
                fteCount: numAny(params, 'fte_count', 'fteCount') ?? 0,
                vacancies: num(params, 'vacancies') ?? 0
            }, opts);
            return {};
        case 'delete_program_company':
            await deliveryClient.deleteProgramCompanyAssignment({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'set_region_owner':
            await deliveryClient.setRegionOwner({
                programId,
                state: str(params, 'state'),
                ownerContactId: strAny(params, 'owner_contact_id', 'ownerContactId'),
                ownerName: strAny(params, 'owner_name', 'ownerName')
            }, opts);
            return {};
        case 'upsert_growth_opportunity':
            await deliveryClient.upsertGrowthOpportunity({
                id: str(params, 'id'),
                programId,
                description: str(params, 'description'),
                status: str(params, 'status'),
                estimatedValue: numAny(params, 'estimated_value', 'estimatedValue') ?? 0,
                comment: str(params, 'comment')
            }, opts);
            return {};
        case 'delete_growth_opportunity':
            await deliveryClient.deleteGrowthOpportunity({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'upsert_subcontractor_review':
            await deliveryClient.upsertSubcontractorReview({
                id: str(params, 'id'),
                programId,
                companyAssignmentId: strAny(params, 'company_assignment_id', 'companyAssignmentId'),
                reviewDate: ymdTimestamp(strAny(params, 'review_date', 'reviewDate')),
                dueDate: ymdTimestamp(strAny(params, 'due_date', 'dueDate')),
                score: num(params, 'score') ?? 0,
                feedback: str(params, 'feedback'),
                state: str(params, 'state'),
                reviewedBy: strAny(params, 'reviewed_by', 'reviewedBy')
            }, opts);
            return {};
        case 'delete_subcontractor_review':
            await deliveryClient.deleteSubcontractorReview({
                id: str(params, 'id')
            }, opts);
            return {};
        case 'detach_touchpoint_file':
            await deliveryClient.detachTouchpointFile({
                programId,
                touchpointId: strAny(params, 'touchpoint_id', 'touchpointId'),
                artifactId: strAny(params, 'artifact_id', 'artifactId')
            }, opts);
            return {};
        case 'upload_project_files':
        case 'upload_award_intake':
        case 'extract_pricing_sheet':
        case 'link_capture_document':
        case 'file_company_document':
        case 'attach_rfi_package':
        case 'save_external_capture_output':
            return applyUploadParityAction(programId, row.action, params, {
                tenantId: options?.tenantId,
                ownerUserId: options?.ownerUserId
            });
        case 'log_capture_interaction':
        case 'record_capture_intel':
        case 'link_capture_competitor':
        case 'link_capture_teaming_partner':
        case 'upsert_capture_task':
        case 'update_capture_profile':
        case 'log_rfi_event':
            return applyCaptureAction(row.action, params, {
                tenantId: options?.tenantId,
                ownerUserId: options?.ownerUserId
            });
        default:
            throw new Error(`Unknown delivery action: ${row.action}`);
    }
}
function normalizeAuditResult(raw) {
    const s = raw.trim();
    if (s === 'N_A' || s === 'NA') return 'N/A';
    return s;
}
/** Feedback line the agent reads after Accept / Reject / Comment / partial apply. */ export function deliveryActionFeedback(event, opts) {
    const parts = [
        `[delivery-action] event=${event}`
    ];
    if (opts.ok !== undefined) parts.push(`ok=${opts.ok}`);
    if (opts.failed !== undefined) parts.push(`failed=${opts.failed}`);
    if (opts.summary) parts.push(stripInternalDutyKeys(opts.summary));
    if (opts.failures?.length) {
        for (const f of opts.failures){
            parts.push(`fail[${f.action}]=${f.message}`);
        }
    }
    if (opts.detail) parts.push(opts.detail);
    return parts.join(' ');
}
/** Strip family duty keys (x001AF) from model-authored summary text for display. */ export function stripInternalDutyKeys(text) {
    return text.replace(/\(?\bx\d+[A-Za-z]*\b\)?/g, '').replace(/\s{2,}/g, ' ').replace(/\s+([,.;:])/g, '$1').trim();
}
/**
 * Human-facing copy for a stored `[delivery-action]…` feedback turn.
 * The raw machine line stays in the message for the agent; ChatMessage
 * renders this instead (same pattern as deliverableImportFeedbackText).
 */ export function deliveryActionFeedbackText(text) {
    const m = text.match(/^\[delivery-action\] event=(\w+)(?:\s+ok=(\d+))?(?:\s+failed=(\d+))?/);
    if (!m) return text;
    const event = m[1];
    const ok = m[2] !== undefined ? Number(m[2]) : undefined;
    const failed = m[3] !== undefined ? Number(m[3]) : undefined;
    switch(event){
        case 'extracted':
            return 'Extracted';
        case 'applied':
            return ok === 1 ? 'Applied: 1 change' : `Applied: ${ok ?? 0} changes`;
        case 'partial':
            return `Partially applied: ${ok ?? 0} ok, ${failed ?? 0} failed`;
        case 'failed':
            return failed === 1 ? 'Failed: 1 change' : `Failed: ${failed ?? 0} changes`;
        case 'rejected':
            return 'Rejected';
        case 'comment':
            return 'Comment sent';
        default:
            return 'Delivery update';
    }
}
function planHeaderCopy(status, ok, failed, open, received) {
    if (status === 'accepted') {
        return {
            text: `Filed ${ok} of ${received} — receipt below`,
            tone: 'success'
        };
    }
    if (status === 'rejected') return {
        text: 'Nothing filed',
        tone: 'danger'
    };
    if (status === 'commented') {
        return {
            text: 'Feedback sent — waiting for Specra Assist',
            tone: 'warning'
        };
    }
    if (failed > 0) {
        return {
            text: `${ok} filed, ${failed} failed — retry the failed rows`,
            tone: 'warning'
        };
    }
    if (ok > 0) {
        return {
            text: `${ok} filed — ${open} ${open === 1 ? 'file waits' : 'files wait'} for your decision`,
            tone: 'warning'
        };
    }
    return {
        text: 'Filing plan — nothing is filed until you accept',
        tone: 'warning'
    };
}
function headerCopy(status, ok, failed, delivery = true) {
    // Pursuit, project and registry cards are not Delivery changes.
    const changes = delivery ? 'Delivery changes' : 'Changes';
    switch(status){
        case 'accepted':
            return {
                text: `${changes} applied via Specra Assist`,
                tone: 'success'
            };
        case 'partial':
            if (ok === 0) {
                return {
                    text: failed === 1 ? 'Failed: 1 change — retry it' : `Failed: ${failed} changes — retry them`,
                    tone: 'warning'
                };
            }
            return {
                text: `Partially applied — ${ok} ok, ${failed} failed — retry remaining`,
                tone: 'warning'
            };
        case 'rejected':
            return {
                text: `${changes} rejected`,
                tone: 'danger'
            };
        case 'commented':
            return {
                text: 'Feedback sent — waiting for Specra Assist',
                tone: 'warning'
            };
        default:
            return {
                text: delivery ? 'Proposed Delivery changes — approval required' : 'Proposed changes — approval required',
                tone: 'warning'
            };
    }
}
/**
 * Approval card for propose-delivery-action: a table of before→after rows,
 * Accept (plus confirm-tier second click), Reject, Comment. Decisions and
 * per-row outcomes persist on the saved proposal via DecideDocumentProposal
 * so a fresh browser/session restores them; partial batches stay retryable
 * for failed rows only. localStorage is not the source of truth.
 */ export function DeliveryActionCard({ output, toolCallId, partId: _partId, requireSavedDecision = false }) {
    void _partId;
    const sendFeedback = useChatFeedback();
    const { user } = useAuth();
    // The glossary page's cache, when the card renders under the app's client.
    const queryClient = useContext(QueryClientContext);
    const acceptorDisplay = (user?.name ?? user?.email ?? '').trim();
    const actions = output.actions ?? [];
    const projectIdsForAuthz = [
        ...new Set(actions.map((a)=>{
            const p = a.params ?? {};
            const v = p.project_id ?? p.projectId;
            return typeof v === 'string' ? v.trim() : '';
        }).filter(Boolean))
    ];
    const initialRows = normalizeRowResults(actions, output.rowResults, output._planFiling);
    const initialStatus = cardStatusFromOutput(output);
    const [status, setStatus] = useState(initialStatus);
    const [confirmArmed, setConfirmArmed] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [showComment, setShowComment] = useState(false);
    const [planPage, setPlanPage] = useState(0);
    const [rowResults, setRowResults] = useState(initialRows);
    const [counts, setCounts] = useState(()=>countRowResults(initialRows));
    const applyingRef = useRef(false);
    const [progress, setProgress] = useState(null);
    // Shared chat files are copied once per card, however many rows use them.
    const fileCache = useRef(new Map());
    const isBulk = Boolean(output.bulk?.length);
    const savedProposal = Boolean(output.approvalConversationId && toolCallId);
    const legacyUnavailable = requireSavedDecision && !savedProposal;
    const programId = output.programId ?? '';
    const needsProgram = actions.some((a)=>deliveryActionNeedsProgram(a.action));
    const authz = useDeliveryUploadAuthz({
        programId,
        projectIds: projectIdsForAuthz,
        actions
    });
    const needsConfirm = output.tier === 'confirm' || actions.some((a)=>a.tier === 'confirm');
    // A plan can name several programs; each row is checked against its own
    // program, exactly as that program's Docs page would be.
    const rowPrograms = output._planFiling ? [
        ...new Set(actions.map((a)=>rowProgramId(a, '')).filter((id)=>id && id !== programId))
    ] : [];
    const [programAccess, setProgramAccess] = useState({});
    const rowVerdict = (row)=>{
        const pid = rowProgramId(row, '');
        if (!pid || pid === programId) return authz.verdict(row.action);
        const meta = deliveryFileMetaByAction[row.action];
        if (!meta) return {
            permitted: true
        };
        const access = programAccess[pid];
        if (!access || access.isLoading) return {
            permitted: false,
            reason: 'Checking permission…'
        };
        return verdictForGate(meta.authzGate, {
            ...access,
            projectCanEdit: false,
            signedIn: Boolean(user?.id)
        });
    };
    const rows = actions.map((row, i)=>{
        const fromParams = strListFiles(row.params ?? {}, 'file_names', 'fileNames');
        const verdict = rowVerdict(row);
        return {
            ...row,
            fileNames: row.fileNames || (fromParams.length ? fromParams.join(', ') : undefined),
            // Always include the index so two rows with the same action (and the
            // same or empty target) never collide — SpecraDataTable warns on dup ids.
            id: `${row.action}-${i}${row.target ? `-${row.target}` : ''}`,
            index: i,
            result: rowResults[i] ?? null,
            permitted: verdict.permitted,
            // The cell already says "Not permitted"; the reason adds only what
            // is new (a loading state, or who can do it).
            notPermittedReason: verdict.permitted ? undefined : [
                verdict.reason === 'Not permitted' ? '' : verdict.reason,
                verdict.whoCan
            ].filter(Boolean).join(' — ')
        };
    });
    useEffect(()=>{
        const next = cardStatusFromOutput(output);
        if (next !== 'pending') {
            setStatus(next);
        }
        const list = output.actions ?? [];
        if (output.rowResults?.length) {
            const normalized = normalizeRowResults(list, output.rowResults, output._planFiling);
            setRowResults(normalized);
            setCounts(countRowResults(normalized));
        }
    }, [
        output
    ]);
    if (!output.success || !actions.length || needsProgram && !programId && !output._planFiling) {
        return /*#__PURE__*/ _jsx("div", {
            className: "rounded-lg border border-danger/30 bg-danger-muted p-3",
            children: /*#__PURE__*/ _jsx(SpecraText, {
                size: "body-sm",
                tone: "danger",
                children: output.error || 'Failed to prepare the Delivery changes.'
            })
        });
    }
    const persistDecision = async (decision, results)=>{
        if (!output.approvalConversationId || !toolCallId) return;
        await agentClient.decideDocumentProposal({
            conversationId: output.approvalConversationId,
            toolCallId,
            decision,
            rowResults: results.map((r)=>({
                    status: r.status,
                    message: r.message ?? '',
                    ...r.apply && Object.keys(r.apply).length > 0 ? {
                        apply: r.apply
                    } : {}
                }))
        });
    };
    /**
   * A bulk card applies its rows a few at a time and saves its progress as it
   * goes. A row someone already handled (a request already open on the date,
   * or already decided) is skipped, never failed, so Retry and a re-accepted
   * card never propose it twice.
   */ const applyBulk = async (indices, isRetry)=>{
        applyingRef.current = true;
        const results = actions.map((_, i)=>rowResults[i] ?? {
                status: 'pending'
            });
        const todo = indices.filter((i)=>{
            const prior = rowResults[i];
            return prior?.status !== 'ok' && prior?.status !== 'skipped';
        });
        let done = 0;
        setProgress({
            done,
            total: todo.length
        });
        await runBulkApply(todo, async (i)=>{
            const preflight = authz.verdict(actions[i].action);
            if (!preflight.permitted) {
                return {
                    status: 'fail',
                    message: [
                        preflight.reason ?? 'Not permitted',
                        preflight.whoCan
                    ].filter(Boolean).join(' — ')
                };
            }
            try {
                await applyDeliveryAction(rowProgramId(actions[i], programId), actions[i], {
                    acceptorDisplay,
                    tenantId: user?.tenant_id,
                    ownerUserId: user?.id,
                    assistRun: output.approvalConversationId && toolCallId ? {
                        conversationId: output.approvalConversationId,
                        toolCallId
                    } : undefined,
                    fileCache: fileCache.current
                });
                return {
                    status: 'ok'
                };
            } catch (err) {
                const already = alreadyHandled(apiErrorMessage(err, ''));
                if (already) return {
                    status: 'skipped',
                    message: already
                };
                return {
                    status: 'fail',
                    message: humanizeDeliveryActionError(err)
                };
            }
        }, {
            onRow: (i, r)=>{
                results[i] = r;
                done++;
                // Repaint every few rows, not 700 times.
                if (done % 10 === 0 || done === todo.length) {
                    setProgress({
                        done,
                        total: todo.length
                    });
                    setRowResults([
                        ...results
                    ]);
                }
            },
            onCheckpoint: async ()=>{
                try {
                    await persistDecision('partial', results);
                } catch  {
                /* the final save below retries */ }
            }
        });
        setProgress(null);
        setRowResults([
            ...results
        ]);
        const counted = countRowResults(results);
        setCounts(counted);
        const failures = results.map((r, i)=>r.status === 'fail' ? {
                action: actions[i].action,
                message: r.message ?? ''
            } : null).filter((f)=>f !== null);
        const open = results.some((r)=>r.status !== 'ok' && r.status !== 'skipped');
        try {
            await persistDecision(open ? 'partial' : 'accepted', results);
        } catch  {
        /* feedback still reports the outcome */ }
        setStatus(open ? 'partial' : 'accepted');
        setConfirmArmed(false);
        applyingRef.current = false;
        sendFeedback(deliveryActionFeedback(open ? counted.ok === 0 ? 'failed' : 'partial' : 'applied', {
            ok: counted.ok,
            failed: counted.failed,
            summary: isRetry ? `${output.summary ?? ''} (retry)`.trim() : output.summary,
            failures: failures.slice(0, 10),
            detail: counted.skipped ? `skipped=${counted.skipped} (already handled)` : undefined
        }));
    };
    const applyIndices = async (indices, isRetry = false, oneRow = false)=>{
        if (legacyUnavailable || applyingRef.current) return;
        if (isBulk) {
            await applyBulk(indices, isRetry);
            return;
        }
        applyingRef.current = true;
        const nextResults = actions.map((_, i)=>{
            if (rowResults[i]) return rowResults[i];
            // Plan skip/identity/conflict rows stay pending until the user files
            // or skips them. Inventing "fail" here made Accept all look broken.
            if (output._planFiling) return {
                status: 'pending'
            };
            return {
                status: 'fail',
                message: ''
            };
        });
        const priorApply = actions.map((_, i)=>rowResults[i]?.apply ?? null);
        const priorStatuses = actions.map((_, i)=>{
            const s = rowResults[i]?.status;
            return s === 'ok' || s === 'fail' ? s : undefined;
        });
        let ok = 0;
        let failed = 0;
        const failures = [];
        for(let i = 0; i < actions.length; i++){
            if (!indices.includes(i)) {
                const prior = nextResults[i];
                if (prior.status === 'ok') ok++;
                else if (prior.status === 'fail') failed++;
                continue;
            }
            // Skip rows that already applied — even when approvalState stayed pending
            // because DecideDocumentProposal failed to persist (reload / retry).
            const prior = rowResults[i];
            const alreadyApplied = prior?.status === 'ok' || prior?.apply != null && Object.keys(prior.apply).length > 0;
            if (alreadyApplied && prior) {
                nextResults[i] = {
                    ...prior,
                    status: 'ok'
                };
                priorApply[i] = prior.apply ?? priorApply[i];
                ok++;
                continue;
            }
            if (output._planFiling && !isPlanWriteRow(actions[i])) {
                nextResults[i] = {
                    status: 'skipped',
                    message: actions[i].skipReason || actions[i].identityQuestion || 'Not filed'
                };
                continue;
            }
            const resolved = resolveDeliveryActionParams(actions[i].params, priorApply, i, priorStatuses);
            if (resolved.error) {
                nextResults[i] = {
                    status: 'fail',
                    message: resolved.error
                };
                priorApply[i] = null;
                failed++;
                failures.push({
                    action: actions[i].action,
                    message: resolved.error
                });
                continue;
            }
            const preflight = authz.verdict(actions[i].action);
            if (!preflight.permitted) {
                const message = [
                    preflight.reason ?? 'Not permitted',
                    preflight.whoCan
                ].filter(Boolean).join(' — ');
                nextResults[i] = {
                    status: 'fail',
                    message
                };
                priorApply[i] = null;
                failed++;
                failures.push({
                    action: actions[i].action,
                    message
                });
                continue;
            }
            try {
                const apply = await applyDeliveryAction(rowProgramId(actions[i], programId), {
                    ...actions[i],
                    params: resolved.params
                }, {
                    acceptorDisplay,
                    tenantId: user?.tenant_id,
                    ownerUserId: user?.id,
                    assistRun: output.approvalConversationId && toolCallId ? {
                        conversationId: output.approvalConversationId,
                        toolCallId
                    } : undefined,
                    fileCache: fileCache.current
                });
                // Every filed file gets a receipt link to where it landed, even when
                // the write RPC returns no id (submission evidence, deviations, …).
                if (!apply.href && deliveryFileMetaByAction[actions[i].action]) {
                    const href = receiptHrefForAction(actions[i].action, {
                        programId,
                        occurrenceId: strAny(resolved.params, 'occurrence_id', 'occurrenceId'),
                        category: str(resolved.params, 'category')
                    });
                    if (href) apply.href = href;
                }
                nextResults[i] = {
                    status: 'ok',
                    apply
                };
                priorApply[i] = apply;
                ok++;
                if (actions[i].action === 'add_glossary_link') {
                    void queryClient?.invalidateQueries({
                        queryKey: glossaryKey(rowProgramId(actions[i], programId))
                    });
                }
            } catch (err) {
                const message = humanizeDeliveryActionError(err);
                nextResults[i] = {
                    status: 'fail',
                    message
                };
                priorApply[i] = null;
                failed++;
                failures.push({
                    action: actions[i].action,
                    message
                });
            }
        }
        setRowResults(nextResults);
        const skipped = nextResults.filter((r)=>r.status === 'skipped').length;
        setCounts({
            ok,
            failed,
            skipped
        });
        const writeOpen = actions.some((action, i)=>{
            if (output._planFiling && !isPlanWriteRow(action)) return false;
            const r = nextResults[i];
            return r.status !== 'ok' && r.status !== 'skipped';
        });
        if (failed > 0 || writeOpen) {
            setStatus(failed > 0 ? 'partial' : 'pending');
            setConfirmArmed(false);
            try {
                await persistDecision('partial', nextResults);
            } catch  {
            /* feedback still reports the outcome */ }
            applyingRef.current = false;
            // A plan row decided one at a time is not news for Specra while other
            // rows still wait: one turn per click buried the card in chatter. Speak
            // when a row failed or the plan is settled.
            if (output._planFiling && oneRow && failed === 0) return;
            sendFeedback(deliveryActionFeedback(ok === 0 && failed > 0 ? 'failed' : 'partial', {
                ok,
                failed,
                summary: output.summary,
                failures
            }));
            return;
        }
        try {
            await persistDecision('accepted', nextResults);
        } catch  {
        /* UI still reflects the apply; reload may re-offer until persist lands */ }
        setStatus('accepted');
        applyingRef.current = false;
        // Extract cards return field values in apply — put them in feedback so the
        // agent can propose upsert_cpars_score / upsert_qasp_score on a new card.
        const extractOnly = actions.length > 0 && actions.every((a)=>(a.action ?? '').startsWith('extract_'));
        const extractDetail = nextResults.map((r, i)=>{
            const action = actions[i]?.action ?? '';
            if (!r.apply || !action.startsWith('extract_')) return '';
            return Object.entries(r.apply).map(([k, v])=>`${k}=${v}`).join(' ');
        }).filter(Boolean).join(' ');
        const intakeDetail = nextResults.map((r, i)=>r.apply?.intake ? `fail[${actions[i]?.action}.intake]=${r.apply.intake}` : '').filter(Boolean).join(' ');
        const receiptDetail = actions.map((a)=>typeof a.href === 'string' && a.href ? `href=${a.href}` : '').filter(Boolean).join(' ');
        sendFeedback(deliveryActionFeedback(extractOnly ? 'extracted' : 'applied', {
            // Extract is not a Delivery write — don't count it as a change.
            ok: extractOnly ? undefined : ok,
            failed: 0,
            summary: isRetry ? `${output.summary ?? ''} (retry succeeded)`.trim() : output.summary,
            detail: [
                extractDetail,
                intakeDetail
            ].filter(Boolean).join(' ') || receiptDetail || undefined
        }));
    };
    const handleAccept = async ()=>{
        if (legacyUnavailable || applyingRef.current) return;
        if (status !== 'pending' && status !== 'partial') return;
        if (needsConfirm && !confirmArmed && status === 'pending') {
            setConfirmArmed(true);
            return;
        }
        const indices = actions.map((_action, i)=>i).filter((i)=>{
            if (output._planFiling && planRowKind(actions[i]) !== 'apply') return false;
            const prior = rowResults[i];
            if (prior?.status === 'ok' || prior?.status === 'skipped') return false;
            // A bulk card saved mid-run has rows still waiting: they go too.
            if (status === 'partial') return prior?.status === 'fail' || isBulk && !isDoneResult(prior);
            return true;
        });
        await applyIndices(indices, status === 'partial');
    };
    const handleAcceptRow = async (index)=>{
        if (legacyUnavailable || applyingRef.current) return;
        await applyIndices([
            index
        ], false, true);
    };
    const handleSkipRow = async (index)=>{
        if (legacyUnavailable || applyingRef.current) return;
        const nextResults = actions.map((_, i)=>{
            if (rowResults[i]) return rowResults[i];
            if (output._planFiling) return {
                status: 'pending'
            };
            return {
                status: 'fail',
                message: ''
            };
        });
        nextResults[index] = {
            status: 'skipped',
            message: actions[index].skipReason || 'Skipped'
        };
        setRowResults(nextResults);
        const counted = countRowResults(nextResults);
        setCounts(counted);
        const writeOpen = actions.some((action, i)=>(!output._planFiling || isPlanWriteRow(action)) && nextResults[i]?.status !== 'ok' && nextResults[i]?.status !== 'skipped');
        // Every write row skipped and nothing filed is a rejection, not an approval.
        const settled = counted.ok > 0 ? 'accepted' : 'rejected';
        setStatus(writeOpen ? counted.failed > 0 ? 'partial' : 'pending' : settled);
        try {
            await persistDecision(writeOpen ? 'partial' : settled, nextResults);
        } catch  {
        /* UI still reflects skip */ }
        if (writeOpen) return;
        if (settled === 'rejected') {
            sendFeedback(deliveryActionFeedback('rejected', {
                summary: output.summary
            }));
            return;
        }
        sendFeedback(deliveryActionFeedback('partial', {
            ok: counted.ok,
            failed: counted.failed,
            summary: output.summary,
            detail: `skipped=${actions[index].fileNames || actions[index].action}`
        }));
    };
    const handleReject = async ()=>{
        if (status !== 'pending' && status !== 'partial' || applyingRef.current) return;
        if (output._planFiling && counts.ok > 0) {
            // Some files are already filed: rejecting would misreport them. Skip
            // what is left and close the plan with its receipt.
            applyingRef.current = true;
            const nextResults = actions.map((_, i)=>{
                const prior = rowResults[i];
                if (prior?.status === 'ok' || prior?.status === 'skipped') return prior;
                return {
                    status: 'skipped',
                    message: 'Skipped'
                };
            });
            setRowResults(nextResults);
            const counted = countRowResults(nextResults);
            setCounts(counted);
            setStatus('accepted');
            try {
                await persistDecision('accepted', nextResults);
            } catch  {
            /* UI still reflects the skip */ }
            applyingRef.current = false;
            sendFeedback(deliveryActionFeedback('partial', {
                ok: counted.ok,
                failed: 0,
                summary: output.summary,
                detail: 'skipped the rest'
            }));
            return;
        }
        applyingRef.current = true;
        try {
            await persistDecision('rejected', rowResults);
        } catch  {
        /* UI still reflects reject */ }
        setStatus('rejected');
        applyingRef.current = false;
        sendFeedback(deliveryActionFeedback('rejected', {
            summary: output.summary
        }));
    };
    const handleComment = ()=>{
        if (!commentText.trim() || status !== 'pending' && status !== 'partial') return;
        setStatus('commented');
        sendFeedback(deliveryActionFeedback('comment', {
            detail: commentText.trim(),
            summary: output.summary
        }));
        setShowComment(false);
    };
    // Staging rate sheets gets its own body: whose sheet, what is in it, and
    // after Accept its progress and a link to its review.
    const pricingStage = !output._planFiling && isPricingStage(actions);
    const bulkWaiting = isBulk && rowResults.some((r)=>r && !isDoneResult(r) && r.status !== 'fail');
    const acceptLabel = status === 'partial' ? bulkWaiting ? 'Continue' : 'Retry failed' : isBulk && !needsConfirm ? `Accept & apply ${actions.length}` : pricingStage ? actions.length === 1 ? 'Accept & stage' : `Accept & stage ${actions.length}` : needsConfirm && !confirmArmed ? 'Accept' : needsConfirm ? 'Yes, do it' : output._planFiling ? 'Accept all' : 'Accept & apply';
    const planOpen = output._planFiling ? rows.filter((r)=>isPlanWriteRow(r) && isPendingResult(r.result)).length : 0;
    const planApplyOpen = output._planFiling ? rows.some((r)=>planRowKind(r) === 'apply' && (isPendingResult(r.result) || r.result?.status === 'fail')) : true;
    const baseHeader = output._planFiling ? planHeaderCopy(status, counts.ok, counts.failed, planOpen, output.received ?? actions.length) : headerCopy(status, counts.ok, counts.failed, needsProgram);
    const header = pricingStage && status === 'pending' ? {
        ...baseHeader,
        text: actions.length === 1 ? 'Add this rate sheet to the registry for review?' : `Add these ${actions.length} rate sheets to the registry for review?`
    } : baseHeader;
    const showActions = (status === 'pending' || status === 'partial') && !legacyUnavailable;
    const writeRows = rows.filter((r)=>!output._planFiling || isPlanWriteRow(r));
    const allBlocked = writeRows.length > 0 && writeRows.every((r)=>r.permitted === false);
    const columns = [
        {
            id: 'destination',
            header: 'Destination',
            cell: (row)=>/*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    "data-testid": Testids.assistant.deliveryActionDestination,
                    tone: row.permitted === false ? 'danger' : undefined,
                    children: row.destination || '—'
                })
        },
        {
            id: 'deliverable',
            header: 'Deliverable',
            cell: (row)=>row.deliverable || '—'
        },
        {
            id: 'date',
            header: 'Date',
            cell: (row)=>row.dueOn ? cmDate(row.dueOn, '—') : '—'
        },
        {
            id: 'team',
            header: 'Team',
            cell: (row)=>row.team || 'Contract-wide'
        },
        {
            id: 'files',
            header: 'Files',
            cell: (row)=>row.fileNames || '—'
        },
        {
            id: 'before',
            header: 'Before',
            cell: (row)=>cardText(row.before) || '—'
        },
        {
            id: 'after',
            header: 'After',
            cell: (row)=>cardText(row.after || row.label || row.action)
        },
        {
            id: 'result',
            header: 'Result',
            cell: (row)=>{
                if (row.result?.status === 'ok') {
                    const href = row.result.apply?.href;
                    return /*#__PURE__*/ _jsxs("span", {
                        className: "flex flex-col gap-[var(--space-0-5)]",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "success",
                                children: "Applied"
                            }),
                            row.href || href ? /*#__PURE__*/ _jsx(SpecraLink, {
                                href: row.href || href,
                                quiet: true,
                                renderLink: renderRouterLink,
                                "data-testid": Testids.assistant.deliveryActionReceipt,
                                children: deliveryActionNeedsProgram(row.action) ? 'Open in Delivery' : 'Open'
                            }) : null,
                            row.result.apply?.intake ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "warning",
                                children: `SOW intake did not start: ${row.result.apply.intake}`
                            }) : null
                        ]
                    });
                }
                if (row.result?.status === 'skipped') {
                    return /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        "data-testid": Testids.assistant.deliveryActionSkipReason,
                        children: row.result.message || row.skipReason || 'Skipped'
                    });
                }
                if (output._planFiling) {
                    const kind = planRowKind(row);
                    const pending = isPendingResult(row.result);
                    const failed = row.result?.status === 'fail';
                    const blocked = row.permitted === false;
                    return /*#__PURE__*/ _jsxs("span", {
                        className: "flex flex-col gap-[var(--space-0-5)]",
                        children: [
                            kind === 'skip' ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: "Not filed"
                            }) : kind === 'identity' ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: "Answer in chat, then plan again"
                            }) : null,
                            blocked ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "danger",
                                "data-testid": Testids.assistant.deliveryActionNotPermitted,
                                children: `Not permitted${row.notPermittedReason ? `: ${row.notPermittedReason}` : ''}`
                            }) : null,
                            failed ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "danger",
                                children: `Failed${row.result?.message ? `: ${row.result.message}` : ''}`
                            }) : null,
                            showActions && pending && isPlanWriteRow(row) ? /*#__PURE__*/ _jsxs("span", {
                                className: "flex gap-[var(--space-1)]",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        type: "button",
                                        size: "sm",
                                        "data-testid": Testids.assistant.deliveryActionAcceptRow,
                                        onClick: ()=>handleAcceptRow(row.index),
                                        disabled: blocked || authz.loading,
                                        children: kind === 'conflict' ? 'File anyway' : 'Accept'
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        type: "button",
                                        size: "sm",
                                        variant: "outline",
                                        "data-testid": Testids.assistant.deliveryActionSkipRow,
                                        onClick: ()=>handleSkipRow(row.index),
                                        children: "Skip"
                                    })
                                ]
                            }) : null,
                            showActions && failed ? /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                size: "sm",
                                "data-testid": Testids.assistant.deliveryActionRetryRow,
                                onClick: ()=>handleAcceptRow(row.index),
                                children: "Retry"
                            }) : null
                        ]
                    });
                }
                if (row.result?.status === 'fail' || row.permitted === false) {
                    const message = row.result?.message || (row.permitted === false ? row.notPermittedReason : undefined);
                    return /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "danger",
                        "data-testid": row.permitted === false ? Testids.assistant.deliveryActionNotPermitted : undefined,
                        children: row.permitted === false && !row.result ? `Not permitted${row.notPermittedReason ? `: ${row.notPermittedReason}` : ''}` : `Failed${message ? `: ${message}` : ''}`
                    });
                }
                // Not applied yet. The row's tool summary is written for the model.
                return /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "Pending"
                });
            }
        }
    ];
    const borderTone = status === 'accepted' ? 'border-success/40' : status === 'rejected' ? 'border-danger/40' : 'border-warning/40';
    const headerBg = status === 'accepted' ? 'border-success/30 bg-success-muted' : status === 'rejected' ? 'border-danger/30 bg-danger-muted' : 'border-warning/30 bg-warning-muted';
    const resultColumn = columns[columns.length - 1];
    // Only columns some row fills; Result once a row has an outcome or a block.
    const shown = visibleOptionalColumns(rows);
    const showResult = rows.some((r)=>r.result || r.permitted === false);
    const tableColumns = columns.filter((c)=>{
        if (c.id === 'result') return showResult;
        if (c.id === 'after') return true;
        return shown.has(c.id);
    });
    const planColumns = [
        {
            id: 'file',
            header: 'File',
            cell: (row)=>/*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    className: "break-all",
                    children: row.fileNames || row.target || '—'
                })
        },
        {
            id: 'type',
            header: 'Type',
            cell: (row)=>/*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    children: planTypeText(row.detectedType, row.confidence)
                })
        },
        {
            id: 'destination',
            header: 'Goes to',
            cell: (row)=>{
                const kind = planRowKind(row);
                if (kind === 'conflict') {
                    return /*#__PURE__*/ _jsxs("span", {
                        className: "flex flex-col gap-[var(--space-0-5)]",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                "data-testid": Testids.assistant.deliveryActionRowKind,
                                children: row.duplicateLabel || 'Already filed'
                            }),
                            row.duplicateHref ? /*#__PURE__*/ _jsx(SpecraLink, {
                                href: row.duplicateHref,
                                quiet: true,
                                renderLink: renderRouterLink,
                                "data-testid": Testids.assistant.deliveryActionDuplicateLink,
                                children: "Open the filed copy"
                            }) : null,
                            isPlanWriteRow(row) && row.destination && row.destination !== '—' ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: `File anyway puts a second copy in ${row.destination}`
                            }) : null
                        ]
                    });
                }
                return /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    "data-testid": Testids.assistant.deliveryActionRowKind,
                    tone: kind === 'apply' && row.permitted === false ? 'danger' : kind === 'apply' ? undefined : 'muted',
                    children: kind === 'identity' ? row.identityQuestion || 'Need a decision' : kind === 'skip' ? `Skipped: ${row.skipReason || 'not fileable'}` : row.destination || '—'
                });
            }
        },
        resultColumn
    ];
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.assistant.deliveryActionCard,
        className: `overflow-hidden rounded-lg border ${borderTone} bg-card`,
        children: [
            rowPrograms.map((pid)=>/*#__PURE__*/ _jsx(ProgramAccessProbe, {
                    programId: pid,
                    onAccess: (access)=>setProgramAccess((prev)=>prev[pid] === access ? prev : {
                                ...prev,
                                [pid]: access
                            })
                }, pid)),
            /*#__PURE__*/ _jsxs("div", {
                className: `flex items-center gap-2 border-b px-3 py-2 ${headerBg}`,
                children: [
                    /*#__PURE__*/ _jsx(ClipboardCheck, {
                        className: `h-4 w-4 ${header.tone === 'success' ? 'text-success' : header.tone === 'danger' ? 'text-danger' : 'text-warning'}`
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        className: "font-medium",
                        tone: header.tone,
                        children: header.text
                    })
                ]
            }),
            output.summary && !pricingStage && /*#__PURE__*/ _jsx("div", {
                className: "border-b border-border px-3 py-2",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "body-sm",
                    children: stripInternalDutyKeys(output.summary)
                })
            }),
            !pricingStage && actions.some((a)=>a.consequence) && /*#__PURE__*/ _jsx("div", {
                className: "border-b border-border px-3 py-2",
                "data-testid": Testids.assistant.deliveryActionConsequence,
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: Array.from(new Set(actions.map((a)=>a.consequence).filter(Boolean))).join(' ')
                })
            }),
            output._planFiling ? /*#__PURE__*/ _jsx(PlanFilingTable, {
                rows: rows,
                columns: planColumns,
                page: planPage,
                onPage: setPlanPage
            }) : pricingStage ? /*#__PURE__*/ _jsx(PricingSheetStageList, {
                rows: rows,
                accepted: status !== 'pending'
            }) : isBulk ? /*#__PURE__*/ _jsx(BulkActionSummary, {
                groups: output.bulk ?? [],
                rows: rows,
                columns: tableColumns,
                started: rowResults.some(Boolean),
                progress: progress
            }) : /*#__PURE__*/ _jsx("div", {
                className: "max-h-64 overflow-auto",
                children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                    columns: tableColumns,
                    rows: rows,
                    getRowId: (row)=>row.id,
                    framed: false
                })
            }),
            output._planFiling ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-[var(--space-0-5)] border-t border-border px-3 py-2",
                "data-testid": Testids.assistant.deliveryActionReceiptSummary,
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        children: planReceiptText({
                            received: output.received ?? actions.length,
                            filed: counts.ok,
                            skipped: (output.skipped ?? 0) + counts.skipped,
                            conflicts: output.conflicts ?? 0,
                            identity: output.identity ?? 0,
                            failed: counts.failed
                        })
                    }),
                    output.followUps?.length ? /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: [
                            "After Accept, you can ",
                            output.followUps.join('; ').toLowerCase(),
                            ". Specra will not run that itself."
                        ]
                    }) : null
                ]
            }) : null,
            needsConfirm && confirmArmed && status === 'pending' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-warning/30 bg-warning-muted px-3 py-2",
                children: /*#__PURE__*/ _jsxs(SpecraText, {
                    size: "meta",
                    tone: "warning",
                    children: [
                        "Confirm: this will ",
                        confirmWords(actions),
                        ". Click “Yes, do it” to apply."
                    ]
                })
            }),
            legacyUnavailable && status === 'pending' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-warning/30 bg-warning-muted px-3 py-2",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "warning",
                    children: "This proposal is missing a saved identity. Ask Specra Assist for a fresh proposal."
                })
            }),
            showActions && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 border-t border-border bg-muted px-3 py-2",
                children: [
                    planApplyOpen ? /*#__PURE__*/ _jsxs(SpecraButton, {
                        type: "button",
                        size: "sm",
                        "data-testid": Testids.assistant.deliveryActionAccept,
                        onClick: handleAccept,
                        disabled: allBlocked || authz.loading,
                        children: [
                            /*#__PURE__*/ _jsx(Check, {
                                className: "h-3.5 w-3.5"
                            }),
                            acceptLabel
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        type: "button",
                        size: "sm",
                        variant: "outline",
                        "data-testid": Testids.assistant.deliveryActionReject,
                        onClick: handleReject,
                        children: [
                            /*#__PURE__*/ _jsx(X, {
                                className: "h-3.5 w-3.5"
                            }),
                            output._planFiling && counts.ok > 0 ? 'Skip the rest' : 'Reject'
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        type: "button",
                        size: "sm",
                        variant: "ghost",
                        onClick: ()=>setShowComment(true),
                        children: [
                            /*#__PURE__*/ _jsx(MessageSquare, {
                                className: "h-3.5 w-3.5"
                            }),
                            "Comment"
                        ]
                    })
                ]
            }),
            showComment && showActions && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 border-t border-border bg-muted px-3 py-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: commentText,
                        onChange: (e)=>setCommentText(e.target.value),
                        placeholder: "Tell Specra Assist what to change...",
                        rows: 2
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setShowComment(false),
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                size: "sm",
                                onClick: handleComment,
                                disabled: !commentText.trim(),
                                children: "Send Feedback"
                            })
                        ]
                    })
                ]
            }),
            status === 'partial' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-warning/30 bg-warning-muted px-3 py-2",
                "data-testid": Testids.assistant.deliveryActionOutcome,
                "data-ok": counts.ok,
                "data-failed": counts.failed,
                "data-skipped": counts.skipped,
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "warning",
                    children: counts.ok === 0 ? `${counts.failed} failed. Retry applies the failed rows.` : `${counts.ok} applied, ${counts.failed} failed. Retry applies only the failed rows.`
                })
            }),
            status === 'accepted' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-success/30 bg-success-muted px-3 py-2",
                "data-testid": Testids.assistant.deliveryActionOutcome,
                "data-ok": counts.ok,
                "data-failed": counts.failed,
                "data-skipped": counts.skipped,
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    className: "font-medium",
                    tone: "success",
                    children: "✓ Changes applied via Specra Assist"
                })
            }),
            status === 'rejected' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-danger/30 bg-danger-muted px-3 py-2",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    className: "font-medium",
                    tone: "danger",
                    children: "✗ Changes rejected"
                })
            }),
            status === 'commented' && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-warning/30 bg-warning-muted px-3 py-2",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    className: "font-medium",
                    tone: "warning",
                    children: "Feedback sent — waiting for Specra Assist"
                })
            })
        ]
    });
}
/** Loads one program's write access for a plan row on another program. */ function ProgramAccessProbe({ programId, onAccess }) {
    const access = useProgramWriteAccess(programId);
    const key = JSON.stringify(access);
    // biome-ignore lint/correctness/useExhaustiveDependencies: report when the access value changes, not on every render
    useEffect(()=>{
        onAccess(access);
    }, [
        key
    ]);
    return null;
}
function planTypeText(detected, confidence) {
    const type = detected?.trim() || '—';
    if (type === '—' || type === 'Unknown') return type === 'Unknown' ? 'Not sure' : type;
    if (confidence === 'likely') return `${type} (likely)`;
    if (confidence === 'uncertain') return `${type} (not sure)`;
    return type;
}
// specra-allow local-composite: groups and pages plan rows over SpecraDataTable so a 1000-member dump is not clipped
function PlanFilingTable({ rows, columns, page, onPage }) {
    const groups = groupPlanRows(rows);
    const flat = groups.flatMap((g)=>g.rows.map((entry)=>({
                ...entry,
                title: g.title
            })));
    const pages = Math.max(1, Math.ceil(flat.length / PLAN_PAGE_SIZE));
    const safePage = Math.min(page, pages - 1);
    const slice = flat.slice(safePage * PLAN_PAGE_SIZE, safePage * PLAN_PAGE_SIZE + PLAN_PAGE_SIZE);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            groups.map((group)=>{
                const visible = slice.filter((s)=>s.title === group.title).map((s)=>s.row);
                if (!visible.length) return null;
                return /*#__PURE__*/ _jsxs("div", {
                    className: "border-b border-border",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "px-3 py-1",
                            children: /*#__PURE__*/ _jsxs(SpecraText, {
                                size: "meta",
                                className: "font-medium",
                                children: [
                                    group.title,
                                    " · ",
                                    group.rows.length
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraDataTable, {
                            columns: columns,
                            rows: visible,
                            getRowId: (row)=>row.id,
                            framed: false
                        })
                    ]
                }, group.kind);
            }),
            pages > 1 ? /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between px-3 py-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "meta",
                        children: [
                            safePage * PLAN_PAGE_SIZE + 1,
                            "–",
                            Math.min(flat.length, (safePage + 1) * PLAN_PAGE_SIZE),
                            ' ',
                            "of ",
                            flat.length
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "flex gap-[var(--space-1)]",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                size: "sm",
                                variant: "outline",
                                disabled: safePage <= 0,
                                onClick: ()=>onPage(safePage - 1),
                                children: "Previous"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                size: "sm",
                                variant: "outline",
                                disabled: safePage >= pages - 1,
                                onClick: ()=>onPage(safePage + 1),
                                children: "Next"
                            })
                        ]
                    })
                ]
            }) : null
        ]
    });
}
function isDoneResult(r) {
    return r?.status === 'ok' || r?.status === 'skipped';
}
/** "approve (×12)" for the confirm line, not twelve copies of it. */ export function confirmWords(actions) {
    const counts = new Map();
    for (const a of actions){
        const label = a.label || a.action;
        counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [
        ...counts.entries()
    ].map(([label, n])=>n > 1 ? `${label} (×${n})` : label).join('; ');
}
